/**
 * Operator API (server-to-server) — sessions, games, players, and transactions.
 *
 * These endpoints are called from your platform's backend. Every request is
 * authenticated with your API key (`Authorization: Bearer <api_key>`) plus an
 * HMAC signature so we can prove the request body was not tampered with.
 *
 * Signing each request:
 *   1. Build the signing string `"<unix_ts_seconds>.<HTTP_METHOD_UPPER> <request_path_and_query>.<raw_json_body>"`.
 *      For GET and DELETE the body segment is the empty string.
 *   2. Compute `signature = "sha256=" + lowercaseHex(HMAC_SHA256(api_secret, signingString))`.
 *   3. Send the headers `X-Repackz-Timestamp: <unix_ts_seconds>` and
 *      `X-Repackz-Signature: <signature>`. The timestamp must be within ±5 minutes
 *      of our server clock or the request is rejected.
 *
 * Use your API key as the Bearer credential and your API secret as the HMAC key;
 * keep the secret on your servers and never expose it to the player's browser.
 * Each operation below includes ready-to-run Ruby, Node.js, and cURL samples.
 */
import { TEMPLATE_NAMES } from '@game-platform/theme-constructor';
import { oddsLevelSchema } from './components';
import { SHIPPING_DEBIT_TAX_SCHEMA } from './components.salesTax';

/** The promotion block on a delivery's money figures (`charge_breakdown` on the
 *  per-player view, `fees` on the operator-wide one). */
const S2S_PROMO_SCHEMA = {
  type: 'object',
  nullable: true,
  description:
    'Set when a shipping promotion lowered this delivery: the fee figures next to this block are then the price the ' +
    'player paid, and this block carries the regular fees they replaced (`original_*`) and the `discount` you funded. ' +
    '`null` when no promotion lowered it.',
  properties: {
    id: { type: 'integer', description: 'Promotion id.' },
    label: { type: 'string', description: 'The promotion name.' },
    covers: {
      type: 'array',
      items: { type: 'string', enum: ['shipping', 'insurance', 'signature', 'handling'] },
      description: 'The parts of the price the promotion covered. A part it did not cover was paid at its regular price.',
    },
    original_shipping: { type: 'number', description: 'Regular shipping fee without the promotion, add-ons included.' },
    original_insurance: { type: 'number', nullable: true, description: 'Regular insurance add-on alone; `0` when the option has none.' },
    original_signature: { type: 'number', nullable: true, description: 'Regular signature add-on alone; `0` when the option has none.' },
    original_handling: { type: 'number', description: 'Regular handling fee without the promotion.' },
    discount: {
      type: 'number',
      description: 'What the promotion took off the regular shipping and handling fees — the amount you fund.',
    },
  },
} as const;

/** One delivery as the S2S shippings endpoints return it — shared between the
 *  per-player list and the cancel response. */
const S2S_SHIPPING_SCHEMA = {
  type: 'object',
  title: 'Shipping',
  properties: {
    id: { type: 'integer', description: 'Delivery (pool_shipping) id.' },
    status: {
      type: 'string',
      enum: ['pending', 'waiting_for_payment', 'paid', 'request_shipping', 'shipped', 'delivered', 'canceled'],
      description: 'Delivery status. `request_shipping` = fulfillment has started (between `paid` and `shipped`).',
    },
    is_allow_to_cancel: {
      type: 'boolean',
      description:
        'True only while the delivery is `paid`, still has unresolved (`pending`) cards and none already shipped — ' +
        'i.e. it can still be canceled via POST /pool_shippings/{id}/cancel. Once fulfillment starts ' +
        '(`request_shipping` and later) or every card is already resolved, this is false and a cancel returns ' +
        '`SHIPPING_NOT_CANCELABLE`.',
    },
    tracking_number: { type: 'string', nullable: true, description: 'Carrier tracking number; null until shipped.' },
    company_name: { type: 'string', nullable: true, description: 'Carrier / shipping company name; null until shipped.' },
    created_at: { type: 'string', format: 'date-time', description: 'When the delivery was created (ISO 8601).' },
    status_updated_at: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: 'When the status last changed (ISO 8601).',
    },
    charge_breakdown: {
      type: 'object',
      nullable: true,
      description:
        'What the player paid when this delivery was charged (`shipping_debit`), in your currency. Null until charged.',
      properties: {
        shipping: { type: 'number', description: 'Includes the chosen insurance/signature option add-on.' },
        insurance: {
          type: 'number',
          nullable: true,
          description:
            'Insurance portion of `shipping` (which stays the full option price). Null for deliveries charged before the breakdown existed.',
        },
        signature: {
          type: 'number',
          nullable: true,
          description: 'Signature portion of `shipping`. Null for deliveries charged before the breakdown existed.',
        },
        handling: { type: 'number' },
        sales_tax: {
          type: 'number',
          nullable: true,
          description: 'Sales tax charged with this delivery; `0`/`null` for operators without sales tax enabled.',
        },
        total: { type: 'number' },
        promo: S2S_PROMO_SCHEMA,
        sales_tax_breakdown: {
          ...SHIPPING_DEBIT_TAX_SCHEMA,
          description:
            'Present only when sales tax applies to this delivery: the tax exactly as the `shipping_debit` carried it ' +
            'as `tax` — integer cents per line, per jurisdiction and per fee.',
        },
      },
    },
    items: {
      type: 'array',
      description: 'The cards in this delivery with their per-card fulfillment status.',
      items: {
        type: 'object',
        title: 'ShippingItem',
        properties: {
          pool_item_id: { type: 'integer', description: 'Card (pool_item) id.' },
          settle_status: {
            type: 'string',
            enum: ['pending', 'shipped', 'unavailable', 'canceled'],
            description:
              '`pending`: not yet resolved. `shipped`: physically fulfilled. `unavailable`: could not be delivered — ' +
              'bought back and refunded. `canceled`: the delivery was canceled — the card was bought back at the regular buyback price.',
          },
          buyback_amount_cents: {
            type: 'integer',
            nullable: true,
            description:
              'Amount (cents, your settlement currency) credited back to the player for this card — the cancel buyback ' +
              'or the unavailable compensation. `null` while the line is unresolved. API-only operators: for a ' +
              '`canceled` line no `/credit` is sent, so use this field to re-credit the player yourself. For an ' +
              '`unavailable` line you still receive `/credit` (`reason: unavailable_item`) — do not also credit from ' +
              'this field, or the player is paid twice.',
          },
        },
      },
    },
  },
} as const;

const S2S_SHIPPING_EXAMPLE = {
  id: 4012,
  status: 'paid',
  is_allow_to_cancel: true,
  tracking_number: null,
  company_name: null,
  created_at: '2026-06-04T10:00:00Z',
  status_updated_at: '2026-06-04T10:00:05Z',
  charge_breakdown: { shipping: 9.92, insurance: 2.5, signature: 1.45, handling: 3.0, sales_tax: null, total: 12.92, promo: null },
  items: [{ pool_item_id: 778812, settle_status: 'pending', buyback_amount_cents: null }],
} as const;

const S2S_SHIPPING_CANCELED_EXAMPLE = {
  ...S2S_SHIPPING_EXAMPLE,
  status: 'canceled',
  is_allow_to_cancel: false,
  status_updated_at: '2026-06-05T09:12:00Z',
  items: [{ pool_item_id: 778812, settle_status: 'canceled', buyback_amount_cents: 720 }],
} as const;

/** One delivery row of the operator-wide list (GET /pool_shippings) — the same
 *  row the Console Deliveries page renders. Money comes from the checkout
 *  snapshot in your currency; internal notes and the handling split are never
 *  included. */
const S2S_DELIVERY_ROW_SCHEMA = {
  type: 'object',
  title: 'DeliveryRow',
  properties: {
    id: { type: 'integer', description: 'Delivery (pool_shipping) id.' },
    created_at: { type: 'string', format: 'date-time', description: 'When the delivery was created (ISO 8601).' },
    status: {
      type: 'string',
      enum: ['pending', 'waiting_for_payment', 'paid', 'request_shipping', 'shipped', 'delivered', 'canceled'],
      description: 'Delivery status. `request_shipping` = fulfillment has started (between `paid` and `shipped`).',
    },
    humanized_status: {
      type: 'string',
      description:
        'Player-facing status text, e.g. "Processing" or "Processing · 1 refunded" — derived from the per-card settle statuses.',
    },
    status_updated_at: { type: 'string', format: 'date-time', nullable: true },
    direction: { type: 'string', enum: ['out'], description: 'Always `out` — only player-bound deliveries are listed.' },
    shipping_type: {
      type: 'string',
      enum: ['delivery', 'smc_transfer'],
      description: '`delivery` = postal shipment, `smc_transfer` = Ship My Cards vault transfer.',
    },
    ship_my_cards_nickname: {
      type: 'string',
      nullable: true,
      description: 'Target SMC account for `smc_transfer` deliveries; null otherwise.',
    },
    source: {
      type: 'object',
      description: 'Your operator record — every row belongs to it.',
      properties: {
        id: { type: 'integer' },
        name: { type: 'string' },
        code: { type: 'string' },
      },
    },
    user_id: { type: 'integer', description: 'Our internal player id (the value the `user_id` filter matches).' },
    operator_account_id: {
      type: 'string',
      nullable: true,
      description: 'The player id as YOUR platform knows it; null when our outcome log cannot attribute the delivery.',
    },
    cards_count: { type: 'integer', description: 'Number of cards in the delivery.' },
    locked_fmv_usd_cents: {
      type: 'integer',
      nullable: true,
      description:
        'Sum of the locked fair market value of every card on the delivery, in USD cents whatever your currency ' +
        'is; null for deliveries older than the outcome log.',
    },
    currency: {
      type: 'string',
      description: 'Your settlement currency — every money figure of the row except `locked_fmv_usd_cents` is in it.',
    },
    total_amount: { type: 'number', description: 'Delivery total — the same figure the Console list shows.' },
    fees: {
      type: 'object',
      nullable: true,
      description:
        'The checkout charge snapshot — what the player actually paid. Null for deliveries charged before the snapshot existed.',
      properties: {
        shipping: { type: 'number' },
        insurance: { type: 'number', nullable: true },
        signature: { type: 'number', nullable: true },
        handling: { type: 'number' },
        sales_tax: {
          type: 'number',
          nullable: true,
          description: 'Sales tax charged with this delivery; `0`/`null` for operators without sales tax enabled.',
        },
        total: { type: 'number' },
        promo: S2S_PROMO_SCHEMA,
      },
    },
    tracking_number: { type: 'string', nullable: true, description: 'Null until the delivery is `shipped`.' },
    tracking_carrier: { type: 'string', nullable: true, description: 'Carrier name; null until `shipped`.' },
    address: {
      type: 'object',
      nullable: true,
      description:
        'List-sized address — enough to recognise the shipment. The detail endpoint returns the full postal record.',
      properties: {
        id: { type: 'integer' },
        name: { type: 'string', nullable: true },
        city: { type: 'string', nullable: true },
        state: { type: 'string', nullable: true },
        zipcode: { type: 'string', nullable: true },
        country: { type: 'string', nullable: true },
      },
    },
  },
} as const;

/** One card inside the delivery detail. */
const S2S_DELIVERY_CARD_SCHEMA = {
  type: 'object',
  title: 'DeliveryCard',
  properties: {
    id: {
      type: 'integer',
      description:
        'Delivery LINE id — the same card travelling again in a later delivery gets a new line. This is the ' +
        'id the "cannot ship" console button addresses.',
    },
    can_report_unavailable: {
      type: 'boolean',
      description: 'Whether this card can currently be reported as unfulfillable.',
    },
    pool_item_id: { type: 'integer', description: 'Card (pool_item) id.' },
    name: { type: 'string', nullable: true, description: 'Card title from the catalog.' },
    grading_company: { type: 'string', nullable: true },
    grade: { type: 'string', nullable: true },
    certificate_number: { type: 'string', nullable: true },
    locked_fmv_usd_cents: {
      type: 'integer',
      nullable: true,
      description: 'Fair market value locked at the round, in USD cents (the currency the outcome log records).',
    },
    round_id: { type: 'integer', nullable: true, description: 'The round (pool_order) the card came out of.' },
    pack_title: { type: 'string', nullable: true, description: 'The pack the card was pulled from — reflects the pack rename set in the console, when one is configured.' },
    settle_status: {
      type: 'string',
      enum: ['pending', 'shipped', 'unavailable', 'canceled'],
      description:
        'Per-card fulfillment. `unavailable` = we could not ship it — bought back and refunded. `canceled` = the ' +
        'delivery was canceled and the card bought back.',
    },
    owner: {
      type: 'object',
      description:
        "Whose stock the card is. Two answers only: yours (`mine: true`, with your operator name) or shared " +
        "(`mine: false`, name null). Another operator's card reads as shared too.",
      properties: {
        mine: { type: 'boolean' },
        name: { type: 'string', nullable: true },
      },
    },
    refund: {
      type: 'object',
      nullable: true,
      description: 'Present once the card was refunded as unavailable: the compensation credited back to the player.',
      properties: {
        amount_cents: { type: 'integer', description: 'In your settlement currency.' },
        amount_usd_cents: { type: 'integer', nullable: true },
        at: { type: 'string', format: 'date-time', nullable: true },
      },
    },
  },
} as const;

/** The full delivery (GET /pool_shippings/{id}) — the row plus the full postal
 *  address, the protection snapshot and the per-card list. */
const S2S_DELIVERY_DETAIL_SCHEMA = {
  type: 'object',
  title: 'DeliveryDetail',
  properties: {
    ...S2S_DELIVERY_ROW_SCHEMA.properties,
    address: {
      type: 'object',
      nullable: true,
      description: 'Full postal record — what support needs to chase a parcel with the carrier.',
      properties: {
        id: { type: 'integer' },
        name: { type: 'string', nullable: true },
        first_line: { type: 'string', nullable: true },
        second_line: { type: 'string', nullable: true },
        apartment_number: { type: 'string', nullable: true },
        city: { type: 'string', nullable: true },
        state: { type: 'string', nullable: true },
        zipcode: { type: 'string', nullable: true },
        country: { type: 'string', nullable: true },
        phone_number: { type: 'string', nullable: true },
      },
    },
    protection: {
      type: 'object',
      nullable: true,
      description:
        'The insurance/signature election snapshotted at checkout; null for deliveries charged before it was recorded.',
      properties: {
        required: { type: 'object', nullable: true, description: 'What the player was required to take.' },
        chosen: { type: 'object', nullable: true, description: 'What the player actually chose.' },
        opted_out: { type: 'boolean', nullable: true },
        opted_out_of: { type: 'array', items: { type: 'string' } },
        acknowledged: { type: 'boolean', nullable: true },
        single_card_over_max: { type: 'boolean', nullable: true },
        insurance_capped: { type: 'boolean', nullable: true },
      },
    },
    cards: { type: 'array', items: S2S_DELIVERY_CARD_SCHEMA },
    refunded_count: { type: 'integer', description: 'How many cards of this delivery were refunded.' },
    settle_summary: {
      type: 'string',
      nullable: true,
      enum: ['refunded', 'partially_shipped'],
      description: 'Final fulfillment override once settled; null while nothing is settled yet.',
    },
  },
} as const;

const S2S_DELIVERY_ROW_EXAMPLE = {
  id: 4012,
  created_at: '2026-06-04T10:00:00Z',
  status: 'paid',
  humanized_status: 'Processing',
  status_updated_at: '2026-06-04T10:00:05Z',
  direction: 'out',
  shipping_type: 'delivery',
  ship_my_cards_nickname: null,
  source: { id: 12, name: 'Your Platform', code: 'your_platform' },
  user_id: 90211,
  operator_account_id: 'player-7788',
  cards_count: 2,
  locked_fmv_usd_cents: 16800,
  currency: 'USD',
  total_amount: 12.92,
  fees: { shipping: 9.92, insurance: 2.5, signature: 1.45, handling: 3.0, sales_tax: null, total: 12.92, promo: null },
  tracking_number: null,
  tracking_carrier: null,
  address: { id: 5511, name: 'John D.', city: 'Austin', state: 'TX', zipcode: '78701', country: 'US' },
} as const;

const S2S_DELIVERY_DETAIL_EXAMPLE = {
  ...S2S_DELIVERY_ROW_EXAMPLE,
  address: {
    id: 5511,
    name: 'John D.',
    first_line: '600 Congress Ave',
    second_line: null,
    apartment_number: 'Apt 4',
    city: 'Austin',
    state: 'TX',
    zipcode: '78701',
    country: 'US',
    phone_number: '+1 512 555 0100',
  },
  protection: {
    required: { insurance: true, signature: false, code_name: 'insurance' },
    chosen: { insurance: true, signature: false },
    opted_out: false,
    opted_out_of: [],
    acknowledged: true,
    single_card_over_max: false,
    insurance_capped: false,
  },
  cards: [
    {
      id: 88001,
      can_report_unavailable: true,
      pool_item_id: 778812,
      name: '2021 Pokémon Charizard VMAX',
      grading_company: 'PSA',
      grade: '10',
      certificate_number: '65001234',
      locked_fmv_usd_cents: 12500,
      round_id: 991201,
      pack_title: 'Pokémon Heat',
      settle_status: 'pending',
      owner: { mine: true, name: 'Your Platform' },
      refund: null,
    },
    {
      id: 88002,
      can_report_unavailable: false,
      pool_item_id: 778813,
      name: '2020 Pikachu Holo',
      grading_company: 'CGC',
      grade: '9.5',
      certificate_number: '77009876',
      locked_fmv_usd_cents: 4300,
      round_id: 991201,
      pack_title: 'Pokémon Heat',
      settle_status: 'unavailable',
      owner: { mine: false, name: null },
      refund: { amount_cents: 4300, amount_usd_cents: 4300, at: '2026-06-05T09:12:00Z' },
    },
  ],
  refunded_count: 1,
  settle_summary: null,
} as const;

export const operatorPaths: Record<string, unknown> = {
  '/loot_machine/api/game_platform/v1/sessions': {
    post: {
      tags: ['Sessions'],
      summary: 'Create a launch session',
      description:
        'Start a play session for one of your players and receive the `launch_url` to open in the ' +
        "player's browser (embed it, redirect to it, or load it inside your own UI).\n\n" +
        'Send the player `token` you issued on your side. We use it to look up the player, confirm their ' +
        'identity and currency, provision their wallet, and enforce a single active session per player.\n\n' +
        '**Idempotent:** if a still-valid session already exists for the same player and `token`, you get ' +
        "that session's `launch_url` back unchanged. Re-launching with the same token keeps the original " +
        'session currency even if you send a different (still validated) context.currency — issue a new ' +
        "token to change the player's currency. Sending a *different* `token` for the same player " +
        'ends the old session and starts a new one.\n\n' +
        'The optional `context.currency` selects the currency for this session from the codes enabled on ' +
        'your account (e.g. `SC`, `USD`). Omit it to use your account default (the first enabled code); a ' +
        'code that is not enabled returns `CURRENCY_MISMATCH` (422). The session currency is echoed in ' +
        'every money webhook (`debit`, `credit`, `refund`, `shipping`) and shown to the player in the game ' +
        'UI. `context.username` and `context.country` remain informational metadata only.\n\n' +
        '**User auto-registration.** Platforms on Stripe funding with registration enabled ' +
        '(`registration_mode=required`) can pass the optional `user` object with their already-authenticated ' +
        'player. When present, the `get_session` webhook is **not called**: `user.account_id` identifies the ' +
        'player directly, and `user.email` (if sent) auto-registers a player account for that email. If the ' +
        'email is already registered to a *different* player on your platform, the session is still created ' +
        "and the success payload carries `warnings: ['email_taken']`. On any other configuration the `user` " +
        'object is ignored and the standard `get_session` flow runs.',
      operationId: 'operatorCreateSession',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'CreateSessionRequest',
              required: ['token'],
              properties: {
                token: {
                  type: 'string',
                  description:
                    "The player's launch token from your platform. Identifies the player and drives " +
                    'idempotency and the single-active-session rule.',
                },
                template: {
                  type: 'string',
                  enum: [...TEMPLATE_NAMES],
                  description:
                    "Visual theme for the play page: 'classic', 'modern', 'sleek' or 'glass'. Chosen per session, so " +
                    'you can launch different players on different templates. Omit it to use your account ' +
                    'default; any other value returns `INVALID_TEMPLATE` (422).',
                },
                game_id: {
                  type: 'string',
                  description:
                    'Id of the game (pack) to open. Passed through to the `launch_url` so the player lands ' +
                    'directly on that pack. Never fails the launch: an id you cannot currently launch — ' +
                    'unknown, not enabled for your account, or not yours — is simply left out of the ' +
                    '`launch_url` and the player lands in the lobby. The ids you can launch are exactly ' +
                    'the ones `GET /v1/games` returns.',
                },
                platform: {
                  type: 'string',
                  description: "Optional launch hint such as 'desktop' or 'mobile'. Informational only.",
                },
                language: {
                  type: 'string',
                  description: 'Optional UI language hint. Informational only.',
                },
                lobby_url: {
                  type: 'string',
                  description:
                    'Optional URL of your lobby. The hosted player experience shows a **Back to lobby** button ' +
                    '(header and session-expired screen) that navigates the top window there. Omit it and the ' +
                    'button is absent.',
                },
                deposit_url: {
                  type: 'string',
                  description:
                    'Optional URL of your cashier. On an insufficient balance the hosted player experience ' +
                    'shows an **Add funds** button that opens it in a new tab; the game stays open and re-reads ' +
                    'the balance when the player returns. See *Handling guide → Insufficient balance*.',
                },
                context: {
                  type: 'object',
                  title: 'SessionContext',
                  description:
                    'Optional player details. `currency` selects the session currency; `username` and ' +
                    '`country` are stored as informational metadata only.',
                  properties: {
                    username: { type: 'string', description: 'Informational display name for the player.' },
                    country: { type: 'string', description: 'Informational country code for the player.' },
                    currency: {
                      type: 'string',
                      description:
                        'Session currency code. Must be one of the codes enabled for your account; ' +
                        'defaults to your account default when omitted.',
                    },
                  },
                },
                user: {
                  type: 'object',
                  title: 'SessionUser',
                  description:
                    'Stripe funding + `registration_mode=required` only: pass your authenticated user and ' +
                    'we auto-register them — the `get_session` webhook is NOT called. `account_id` is ' +
                    'required within this object. On any other configuration the object is ignored.',
                  required: ['account_id'],
                  properties: {
                    account_id: {
                      type: 'string',
                      description:
                        "The player's stable account id on your platform. Required within `user`; it plays " +
                        'the role `get_session` normally fills.',
                    },
                    email: {
                      type: 'string',
                      format: 'email',
                      description:
                        "The player's email. When sent, we register (or match) a player account for it; if " +
                        'it already belongs to a different player on your platform, the session is still ' +
                        "created and the success payload carries `warnings: ['email_taken']`.",
                    },
                    username: { type: 'string', description: 'Informational display name for the player.' },
                    country: { type: 'string', description: 'Informational country code for the player.' },
                  },
                },
                is_test: {
                  type: 'boolean',
                  default: false,
                  description:
                    'Marks the launched player as a test player. Only available when the test-player ' +
                    'registry is enabled for your operator account (`allow_test_player_registry`). When ' +
                    'true, the player is added to (or reactivated in) your test-player registry and their ' +
                    'activity is excluded from GGR, reports and settlements. Sending false — or omitting ' +
                    'the field — never removes an existing mark. If the registry is not enabled for your ' +
                    'account, the flag is ignored and the session is created normally with a ' +
                    '`test_player_registry_disabled` warning.',
                },
              },
            },
            example: {
              token: 'op-session-abc123',
              template: 'modern',
              game_id: '5',
              platform: 'desktop',
              language: 'en',
              lobby_url: 'https://platform.example.com/lobby',
              deposit_url: 'https://platform.example.com/cashier',
              context: { username: 'player_one', country: 'US', currency: 'USD' },
              is_test: false,
            },
          },
        },
      },
      responses: {
        '201': {
          description: 'Session created, or an existing valid session returned idempotently.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'CreateSessionResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['launch_url', 'session_token', 'currency', 'expires_at'],
                    properties: {
                      launch_url: {
                        type: 'string',
                        description:
                          "The URL to open in the player's browser. It already carries the session and the " +
                          'chosen game so the player lands on the right pack.',
                      },
                      session_token: {
                        type: 'string',
                        description:
                          'The session identifier for this launch. Use it to end the session via ' +
                          'DELETE /sessions/{token}. This is distinct from the player `token` you sent.',
                      },
                      currency: {
                        type: 'string',
                        description:
                          'The session currency (echoes the resolved context.currency or your account default).',
                      },
                      expires_at: {
                        type: 'string',
                        format: 'date-time',
                        description: 'When the session expires (ISO 8601). Sessions are valid for 24 hours.',
                      },
                    },
                  },
                  warnings: {
                    type: 'array',
                    items: {
                      type: 'string',
                      enum: ['email_taken', 'test_player_registry_disabled', 'test_player_registration_failed'],
                    },
                    description:
                      'Non-fatal notices — the session is still created in every case. `email_taken`: on ' +
                      'the user auto-registration path, `user.email` is already registered to a different ' +
                      'player on your platform. `test_player_registry_disabled`: `is_test: true` was sent ' +
                      'but the test-player registry is not enabled for your account, so the flag was ' +
                      'ignored. `test_player_registration_failed`: the player could not be added to your ' +
                      'test-player registry — the session works, but the player is NOT marked as a test ' +
                      'player.',
                  },
                },
              },
              example: {
                result: {
                  launch_url: 'https://app.repackz.fun?session_token=Yl9kF3oQb2c1...32bytes...&game_id=5',
                  session_token: 'Yl9kF3oQb2c1d4eR5tY6uI7oP8aS9dF0gH1jK2lZ',
                  currency: 'USD',
                  expires_at: '2026-05-30T17:31:00Z',
                },
              },
            },
          },
        },
        '401': {
          description:
            'Authentication failed — `INVALID_API_KEY` (missing or unknown API key), `INVALID_SIGNATURE` ' +
            '(missing or mismatched HMAC headers), or `EXPIRED_TIMESTAMP` (timestamp outside the ±5 minute window).',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            'Request rejected — `INVALID_TEMPLATE` (unknown theme), `CURRENCY_MISMATCH` (context.currency ' +
            'not enabled for your account), `OPERATOR_AUTHENTICATION_FAIL` (we could not confirm the ' +
            "player with your platform), `OPERATOR_INVALID_RESPONSE` (your platform returned no player " +
            'id), or `SESSION_CREATE_FAILED`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'json'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "path = '/loot_machine/api/game_platform/v1/sessions'",
            "body = JSON.generate(token: 'op-session-abc123', template: 'modern', game_id: '5')",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.POST #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Post.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            "req['Content-Type'] = 'application/json'",
            'req.body = body',
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const path = '/loot_machine/api/game_platform/v1/sessions';",
            "const body = JSON.stringify({ token: 'op-session-abc123', template: 'modern', game_id: '5' });",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.POST ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'POST',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            "    'Content-Type': 'application/json',",
            '  },',
            '  body,',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/sessions'",
            'BODY=\'{"token":"op-session-abc123","template":"modern","game_id":"5"}\'',
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.POST $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS -X POST "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG" \\',
            "  -H 'Content-Type: application/json' \\",
            '  -d "$BODY"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/sessions/{token}': {
    delete: {
      tags: ['Sessions'],
      summary: 'End a session',
      description:
        'End a play session from your platform — for example on logout, self-exclusion, or a KYC block.\n\n' +
        'On success you receive **HTTP 204 No Content** with an empty body. This call is **idempotent**: ' +
        'ending an already-ended or expired session still returns 204. An unknown session returns 404.\n\n' +
        'The `{token}` here is the `session_token` returned by the create-session call, not the player ' +
        '`token` you originally sent.',
      operationId: 'operatorEndSession',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'token',
          in: 'path',
          required: true,
          schema: { type: 'string' },
          description: 'The `session_token` returned when the session was created.',
        },
      ],
      responses: {
        '204': {
          description: 'Session ended (or already ended). Empty body.',
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: '`NOT_FOUND` — no session with that `session_token` belongs to your account.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "session_token = 'Yl9kF3oQb2c1d4eR5tY6uI7oP8aS9dF0gH1jK2lZ'",
            'path = "/loot_machine/api/game_platform/v1/sessions/#{session_token}"',
            "body = '' # DELETE has no body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.DELETE #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Delete.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.code # 204 on success',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const sessionToken = 'Yl9kF3oQb2c1d4eR5tY6uI7oP8aS9dF0gH1jK2lZ';",
            'const path = `/loot_machine/api/game_platform/v1/sessions/${sessionToken}`;',
            "const body = ''; // DELETE has no body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.DELETE ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'DELETE',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(res.status); // 204 on success',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "SESSION_TOKEN='Yl9kF3oQb2c1d4eR5tY6uI7oP8aS9dF0gH1jK2lZ'",
            'PATH_URI="/loot_machine/api/game_platform/v1/sessions/$SESSION_TOKEN"',
            "BODY='' # DELETE has no body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.DELETE $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS -i -X DELETE "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/games': {
    get: {
      tags: ['Games (S2S)'],
      summary: 'List available games',
      description:
        'Return every game (pack) currently available for purchase, so you can build your lobby or game ' +
        'catalog. The full list is returned in one response — there is no pagination or filtering.\n\n' +
        'Prices are in minor units (cents), and `currency` reflects your account currency. `game_name` and ' +
        '`game_title` carry the same display title.\n\n' +
        '`game_type` is `loot_pack` for a regular pack and `custom_pack` for a Custom Pack Lab game, where the ' +
        'player picks the cards and sets their chances. A `custom_pack` game has no fixed price (`price_cents` is ' +
        '`null`: the price is set per purchase) and no odds table (`odds` is empty, `min_value` and `max_value` are 0, ' +
        'and `odds_levels` is `null`). Custom packs are not provably fair, so they are left out of the list when ' +
        'your platform requires provably fair.\n\n' +
        'Each game also carries its reveal `odds` (per value tier) and the `min_value`/`max_value` ' +
        'card-value range in USD, both derived from the odds tiers.\n\n' +
        'For a pack you price yourself in the console (your own estimated value, RTP per volatility level, or RTP ' +
        'and win rate per volatility level), `odds`, `min_value`/`max_value` and `metadata.estimated_value` follow ' +
        'your pricing: they describe what the pack actually draws at Normal volatility, the same table as ' +
        '`odds_levels.normal` (the odds are cached for up to 60 seconds), whenever that per-level breakdown is ' +
        'available for the pack. When it is not (`odds_levels` would be `null` for it), `odds` and ' +
        "`min_value`/`max_value` are the pack's default while `metadata.estimated_value` still follows your pricing. " +
        'Every other pack carries its default odds and estimated value.\n\n' +
        "`badge` is the pack's badge label (e.g. \"Gold\") — the badge you set for the pack in the console when " +
        "one is configured, otherwise the pack's default badge, or `null` when it has none.\n\n" +
        "Pass `include_example_item=true` to also get each pack's headline card in `example_item` — the most " +
        'valuable card currently pullable within its odds tiers (picked by the same rule as **List most expensive ' +
        'cards** with `game_id=<id>&limit=1`), with `title`, `image_url` and, when `include_card_details=true` is ' +
        'passed as well, `card_details`. It is served from a background cache that is refreshed once an entry is ' +
        '15 minutes old, so it can lag the live pool by roughly 15 minutes. `example_item` is `null` without the ' +
        'flag, while a pack is still warming up (on the first poll that asks for it, or after 24 hours without one — ' +
        'the next poll usually has it), or when the pack has no eligible card.\n\n' +
        'Pass `with_odds_levels=true` to also get `odds_levels` on each game — the per-volatility odds (Normal / High / ' +
        'Max); when present, it is the same object the Player API game list returns with the same flag. Each level ' +
        'reflects your configuration for the pack, i.e. what is actually drawn at that volatility. `odds_levels` is ' +
        '`null` for a game whose per-level breakdown is not available in this listing (for example, a pack whose ' +
        'odds table is static, or one with no odds configured). The breakdown is cached for up to 60 seconds. Without the ' +
        'flag the key is absent.',
      operationId: 'operatorListGames',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        { $ref: '#/components/parameters/IncludeCardDetails' },
        { $ref: '#/components/parameters/IncludeExampleItem' },
        { $ref: '#/components/parameters/WithOddsLevels' },
      ],
      responses: {
        '200': {
          description:
            'Catalog of available games. The example shows `example_item` as returned with ' +
            '`include_example_item=true`; without the flag it is `null`.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'GameListResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['games'],
                    properties: {
                      games: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'Game',
                          properties: {
                            game_id: { type: 'integer', description: 'Unique id of the game (pack).' },
                            game_name: {
                              type: 'string',
                              description:
                                'Display title of the game. The name shown to players — reflects the pack rename set in the console, when one is configured.',
                            },
                            game_title: { type: 'string', description: 'Display title of the game (same as game_name).' },
                            game_type: {
                              type: 'string',
                              enum: ['loot_pack', 'custom_pack'],
                              description:
                                "Game format: 'loot_pack' for a regular pack, 'custom_pack' for a Custom Pack Lab game " +
                                'where the player picks the cards and sets their chances.',
                            },
                            category: { type: 'string', description: "Category name; defaults to 'packs' when none is set." },
                            description: { type: 'string', nullable: true, description: 'Marketing description; may be null.' },
                            price_cents: {
                              type: 'integer',
                              nullable: true,
                              description:
                                'Pack price in minor units (cents). null for custom_pack — the price is set per purchase.',
                            },
                            currency: {
                              type: 'string',
                              nullable: true,
                              description: "Your account currency for the price (e.g. 'USD').",
                            },
                            min_value: {
                              type: 'number',
                              description:
                                'Floor of the lowest odds tier in USD. 0 when no odds are configured, and for a custom_pack game.',
                            },
                            max_value: {
                              type: 'number',
                              description:
                                'Top edge of the highest odds tier in USD (its lower edge when that tier is open-ended). ' +
                                '0 when no odds are configured, and for a custom_pack game.',
                            },
                            odds: {
                              type: 'array',
                              description:
                                'Per-tier reveal odds, sorted from the lowest value tier upward. For a pack you price yourself in the console, ' +
                                'the odds it draws with at Normal volatility under your pricing (the same rows as `odds_levels.normal.tiers`) ' +
                                "when that breakdown is available, otherwise the pack's default odds. " +
                                'Empty when no odds are configured, and for a custom_pack game.',
                              items: {
                                type: 'object',
                                title: 'OddsTier',
                                required: ['tier_name', 'range', 'percentage'],
                                properties: {
                                  tier_name: { type: 'string', description: 'Tier label, e.g. "Common", "Uncommon", "Legendary".' },
                                  range: { type: 'string', description: 'Formatted value range for the tier, e.g. "$0 - $50" or "$500+".' },
                                  percentage: { type: 'number', description: 'Probability of pulling a card in this tier, as a percentage.' },
                                },
                              },
                            },
                            thumbnail_url: {
                              type: 'string',
                              nullable: true,
                              description: 'Pack artwork URL; may be null.',
                            },
                            badge: {
                              type: 'object',
                              title: 'GameBadge',
                              nullable: true,
                              description:
                                "The pack's badge — reflects the badge you set for the pack in the console when one is configured, " +
                                "otherwise the pack's default badge. Null when the pack has no badge.",
                              required: ['name'],
                              properties: {
                                name: { type: 'string', description: 'Badge label, e.g. "Gold".' },
                              },
                            },
                            metadata: {
                              type: 'object',
                              title: 'GameMetadata',
                              properties: {
                                estimated_value: {
                                  type: 'number',
                                  nullable: true,
                                  description:
                                    'Estimated value of one pack in USD at Normal volatility. For a pack you price yourself in the console it ' +
                                    'is the value your pricing pays; with RTP and win rate per volatility level, the pack price × your Normal ' +
                                    'RTP, not rounded to cents (e.g. 9.525 for 95.25% on a $10 pack). Otherwise the pack\'s default. Null for a ' +
                                    'custom_pack game, and may be null when the pack has none.',
                                },
                                slug: { type: 'string', nullable: true, description: 'URL-friendly identifier; may be null.' },
                              },
                            },
                            example_item: {
                              type: 'object',
                              title: 'ExampleCard',
                              nullable: true,
                              description:
                                "The pack's headline card — the most valuable card currently pullable within its odds tiers, " +
                                'picked by the same rule as **List most expensive cards** with `game_id=<id>&limit=1`. Populated only ' +
                                'when `include_example_item=true`, from a background cache that is refreshed once an entry is ' +
                                '15 minutes old, so it can lag the live pool by roughly 15 minutes. Null without the flag, while ' +
                                'the pack is still warming up (on the first poll that asks for it, or after 24 hours without one — ' +
                                'the next poll usually has it), or when the pack has no eligible card.',
                              properties: {
                                title: { type: 'string', description: 'Card title.' },
                                image_url: { type: 'string', format: 'uri', nullable: true, description: 'Card image URL; may be null.' },
                                card_details: { $ref: '#/components/schemas/CardDetails' },
                              },
                            },
                            odds_levels: {
                              type: 'object',
                              title: 'OddsLevels',
                              nullable: true,
                              description:
                                'Per-volatility-level odds (Normal / High / Max) for this game, reflecting your configuration ' +
                                'for the pack. Only present when the request is sent with `with_odds_levels=true`; cached for up ' +
                                'to 60 seconds. Null when the per-level breakdown is not available in this listing (for example, ' +
                                'a pack whose odds table is static, one with no odds configured, or a custom_pack game). When present, ' +
                                'the same object as `odds_levels` in the Player API game list. For a pack you price yourself in the ' +
                                'console, `normal` carries the same rows as the top-level `odds`.',
                              required: ['normal', 'high', 'max'],
                              properties: {
                                normal: oddsLevelSchema,
                                high: oddsLevelSchema,
                                max: oddsLevelSchema,
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  games: [
                    {
                      game_id: 5,
                      game_name: 'HOF ELITE MMA Pack',
                      game_title: 'HOF ELITE MMA Pack',
                      game_type: 'loot_pack',
                      category: 'packs',
                      description: 'Premium MMA collectible pack',
                      price_cents: 1999,
                      currency: 'USD',
                      min_value: 5,
                      max_value: 500,
                      odds: [
                        { tier_name: 'Common', range: '$5 - $50', percentage: 78.5 },
                        { tier_name: 'Uncommon', range: '$50 - $200', percentage: 18 },
                        { tier_name: 'Legendary', range: '$500+', percentage: 3.5 },
                      ],
                      thumbnail_url: 'https://cdn.repackz.fun/rails/active_storage/.../pack.png',
                      badge: { name: 'Gold' },
                      metadata: { estimated_value: 24.5, slug: 'hof-elite-mma-pack' },
                      example_item: {
                        title: 'Conor McGregor — Championship Auto',
                        image_url: 'https://cdn.repackz.fun/rails/active_storage/.../card.png',
                      },
                    },
                    {
                      game_id: 612,
                      game_name: 'Custom Pack Lab',
                      game_title: 'Custom Pack Lab',
                      game_type: 'custom_pack',
                      category: 'packs',
                      description: 'Pick your cards, set their chances, rip one.',
                      price_cents: null,
                      currency: 'USD',
                      min_value: 0,
                      max_value: 0,
                      odds: [],
                      thumbnail_url: 'https://cdn.repackz.fun/rails/active_storage/.../custom-pack.png',
                      badge: null,
                      metadata: { estimated_value: null, slug: 'custom-pack-lab' },
                    },
                  ],
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "path = '/loot_machine/api/game_platform/v1/games'",
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const path = '/loot_machine/api/game_platform/v1/games';",
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/games'",
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/items': {
    get: {
      tags: ['Items (S2S)'],
      summary: 'List most expensive cards',
      description:
        'Return the most valuable cards a player can currently pull across your enabled packs — handy for a ' +
        '"top prizes" showcase in your lobby. `sort` selects the ordering (only `most_expensive` today); ' +
        '`game_id`, `min_value` and `limit` narrow the result.\n\n' +
        'Card `value` is in USD. Each card carries the pack it belongs to (`game_id` / `game_name`).',
      operationId: 'operatorListItems',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'sort',
          in: 'query',
          required: false,
          schema: { type: 'string', enum: ['most_expensive'], default: 'most_expensive' },
          description: 'Ordering of the cards. Currently only `most_expensive` (highest value first).',
        },
        {
          name: 'game_id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Limit to a single pack. Cards are returned only when that pack is enabled for your account.',
        },
        {
          name: 'min_value',
          in: 'query',
          required: false,
          schema: { type: 'number' },
          description: 'Only return cards worth at least this much (USD).',
        },
        {
          name: 'limit',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 20, maximum: 50 },
          description: 'How many cards to return. Defaults to 20, capped at 50.',
        },
      ],
      responses: {
        '200': {
          description: 'Cards ordered by the chosen sort.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'ItemListResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['items'],
                    properties: {
                      items: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'Item',
                          properties: {
                            id: { type: 'integer', description: 'Unique id of the card.' },
                            title: { type: 'string', description: 'Card title.' },
                            image_url: {
                              type: 'string',
                              format: 'uri',
                              nullable: true,
                              description: 'Card image URL; may be null.',
                            },
                            value: { type: 'number', nullable: true, description: 'Card value in USD.' },
                            game_id: { type: 'integer', description: 'Id of the pack this card belongs to.' },
                            game_name: {
                              type: 'string',
                              nullable: true,
                              description:
                                'Name of the pack this card belongs to — reflects the pack rename set in the console, when one is configured.',
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  items: [
                    {
                      id: 90123,
                      title: 'Conor McGregor — Championship Auto',
                      image_url: 'https://cdn.repackz.fun/rails/active_storage/.../card.png',
                      value: 500.0,
                      game_id: 5,
                      game_name: 'HOF ELITE MMA Pack',
                    },
                  ],
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "path = '/loot_machine/api/game_platform/v1/items?sort=most_expensive&limit=10'",
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const path = '/loot_machine/api/game_platform/v1/items?sort=most_expensive&limit=10';",
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/items?sort=most_expensive&limit=10'",
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/jackpot_cards': {
    get: {
      tags: ['Items (S2S)'],
      summary: 'List active jackpot cards',
      description:
        'Return the high-value cards you have pre-purchased and flagged to your account as **jackpot ' +
        'cards**. Each jackpot card belongs to exactly one operator and can only be awarded to that ' +
        "operator's players — they are never shared across platforms.\n\n" +
        'A card stays in this list while its `status` is `active`. Once it has been awarded to a player ' +
        'it flips to `won` and leaves the active set. If the player later sells the card back (manually ' +
        'or via auto-sell), it automatically returns to your pool as `active` — same id — and can be ' +
        'awarded again.\n\n' +
        'To award a jackpot card, return its `id` as `jackpot.card_id` in your `debit` callback response ' +
        '(see the Wallet Callbacks → *Take funds to buy a pack* webhook): we place that specific card ' +
        'into the pack, replacing one normal pull. API-only integrations do not receive `/debit` — they ' +
        'pass the same `jackpot.card_id` instruction in the body of the buy-a-pack (instant purchase) ' +
        'request instead. A `custom_pack` round never awards a jackpot: the instruction is ignored in the ' +
        '`debit` response and refused (`422 JACKPOT_NOT_ALLOWED`) on the instant purchase. ' +
        '**Eligibility and win probability are entirely ' +
        'operator-side** — you decide which player, and on which purchase, a jackpot card is awarded; we ' +
        'only validate the id and a runaway guard: at most **3000 jackpot awards per operator per ' +
        'calendar day (UTC)**. That ceiling exists to stop a looping retry, not to pace your promotions — ' +
        'how often a jackpot is awarded is yours to decide. An instruction beyond the ceiling is skipped ' +
        '(the purchase succeeds with a normal pull instead), and a sell-back does not free up an award, ' +
        'because the count is read from our append-only award journal.\n\n' +
        'Card `market_value` is in USD and `currency` reflects your account currency. Use `status` to ' +
        'inspect already-won cards.\n\n' +
        'You register and de-register jackpot cards yourself: `POST /v1/jackpot_cards` marks one of your ' +
        'cards, `DELETE /v1/jackpot_cards/{id}` takes it back out. Your inventory feed ' +
        '(`GET /v1/pool_items`) carries `is_jackpot_card` and `jackpot_card_id` on every row, so the two ' +
        'lists can be joined without a second lookup.',
      operationId: 'operatorListJackpotCards',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'status',
          in: 'query',
          required: false,
          schema: { type: 'string', enum: ['active', 'won'], default: 'active' },
          description:
            'Which jackpot cards to return. `active` (default) are still available to award; `won` have already been awarded.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1 },
          description: '1-based page number.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Page size. Defaults to 20 and is capped at 200.',
        },
      ],
      responses: {
        '200': {
          description: 'Your jackpot cards for the requested status.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'JackpotCardListResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['jackpot_cards', 'meta'],
                    properties: {
                      jackpot_cards: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'JackpotCard',
                          properties: {
                            id: {
                              type: 'integer',
                              description:
                                'Unique id of the jackpot card. Return this as `jackpot.card_id` in your debit response to award it.',
                            },
                            name: { type: 'string', description: 'Display name of the card.' },
                            image_url: {
                              type: 'string',
                              format: 'uri',
                              nullable: true,
                              description: 'Card image URL; may be null.',
                            },
                            market_value: { type: 'number', description: 'Fair market value of the card, in USD.' },
                            currency: { type: 'string', description: "Your account currency for the value (e.g. 'USD')." },
                            status: {
                              type: 'string',
                              enum: ['active', 'won'],
                              description: '`active` while still available to award; `won` once it has been awarded to a player.',
                            },
                          },
                        },
                      },
                      meta: {
                        type: 'object',
                        title: 'JackpotCardListMeta',
                        required: ['page', 'total_count'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          total_count: { type: 'integer', description: 'Total matching jackpot cards across all pages.' },
                        },
                        description: 'Pagination details for this list.',
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  jackpot_cards: [
                    {
                      id: 90123,
                      name: 'LeBron James — Logoman 1/1',
                      image_url: 'https://cdn.repackz.fun/rails/active_storage/.../lebron.png',
                      market_value: 82000,
                      currency: 'USD',
                      status: 'active',
                    },
                    {
                      id: 90124,
                      name: 'Luka Dončić — Gold Prizm Auto /10',
                      image_url: 'https://cdn.repackz.fun/rails/active_storage/.../luka.png',
                      market_value: 14500,
                      currency: 'USD',
                      status: 'active',
                    },
                  ],
                  meta: { page: 1, total_count: 2 },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "path = '/loot_machine/api/game_platform/v1/jackpot_cards?status=active'",
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const path = '/loot_machine/api/game_platform/v1/jackpot_cards?status=active';",
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/jackpot_cards?status=active'",
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },

    post: {
      tags: ['Items (S2S)'],
      summary: 'Mark a card as a jackpot card',
      description:
        'Register one of your cards as a jackpot card, without asking us to do it for you. Pass the ' +
        '`pool_item_id` exactly as it appears in `GET /v1/pool_items`; the response carries the ' +
        '`jackpot_card.id` you then echo back as `jackpot.card_id` when you decide to award it.\n\n' +
        '**Only stock loaded under your own operator can be marked.** Any other card — another ' +
        "operator's, or shared inventory nobody pre-funded for you — answers `404 POOL_ITEM_NOT_FOUND`, " +
        'and only cards still in `listed` status qualify: a card already in a player\'s hands or on its ' +
        'way to shipping can no longer be awarded.\n\n' +
        'Marking is **not a cosmetic label**. From that moment the card leaves every draw, the pack\'s ' +
        "published odds and its stock counts — it becomes yours to award and nobody else's to pull. If " +
        'the card is still linked to a sellable pack, the call succeeds and the response carries a ' +
        '`warning`: it stays a member of that pack until it is unlinked by hand, even though it can no ' +
        'longer be drawn from it.\n\n' +
        'The call is idempotent: marking a card you already marked returns the same jackpot card. A card ' +
        'you previously unmarked comes back with its original id. Rate limit: 20 writes per minute.\n\n' +
        'This response is the authoritative result — it already carries the `jackpot_card.id`. The ' +
        'inventory feed is served from a read replica, so a card marked a moment ago can still read as ' +
        '`is_jackpot_card: false` there for a short while; do not treat that as the mark having failed.',
      operationId: 'operatorMarkJackpotCard',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'MarkJackpotCardRequest',
              required: ['pool_item_id'],
              properties: {
                pool_item_id: {
                  type: 'integer',
                  description: 'The card to mark, as `id` in `GET /v1/pool_items`. Must be your own stock and `listed`.',
                },
              },
            },
            example: { pool_item_id: 8508931 },
          },
        },
      },
      responses: {
        '200': {
          description: 'The card is now a jackpot card.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'MarkJackpotCardResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['jackpot_card'],
                    properties: {
                      jackpot_card: {
                        type: 'object',
                        title: 'MarkedJackpotCard',
                        properties: {
                          id: {
                            type: 'integer',
                            description: 'Jackpot card id — echo this back as `jackpot.card_id` to award the card.',
                          },
                          name: { type: 'string', description: 'Display name of the card.' },
                          image_url: { type: 'string', format: 'uri', nullable: true, description: 'Card image URL; may be null.' },
                          market_value: { type: 'number', description: 'Fair market value of the card, in USD.' },
                          currency: { type: 'string', description: 'Your account currency for the value.' },
                          status: { type: 'string', enum: ['active'], description: 'Always `active` for a freshly marked card.' },
                          warning: {
                            type: 'string',
                            nullable: true,
                            description:
                              'Present when the card is still linked to a sellable pack: it can no longer be drawn and no ' +
                              'longer counts towards that pack\'s odds or stock, but the link itself remains until removed. ' +
                              'Null when the card is already isolated.',
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  jackpot_card: {
                    id: 90125,
                    name: '2018 Panini Kaboom! Shaquille O\'Neal PSA 9',
                    image_url: 'https://cdn.repackz.fun/rails/active_storage/.../shaq.png',
                    market_value: 9000,
                    currency: 'USD',
                    status: 'active',
                    warning: null,
                  },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description:
            '`POOL_ITEM_NOT_FOUND` — no card of yours with that id. Cards belonging to another operator, and shared ' +
            'inventory, answer the same way.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '409': {
          description: '`JACKPOT_CARD_CONFLICT` — the card is already registered as a jackpot card for another operator.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            '`POOL_ITEM_ID_REQUIRED` — the body carried no `pool_item_id`. `POOL_ITEM_ID_INVALID` — `pool_item_id` was not ' +
            'a single numeric id (a list of ids is rejected rather than resolved to an arbitrary one). ' +
            '`POOL_ITEM_NOT_LISTED` — the card is no longer listed stock (already with a player, quarantined, or shipped). ' +
            '`JACKPOT_CARD_ALREADY_WON` — the card was already awarded.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '429': {
          description: 'Rate limit exceeded — at most 20 jackpot writes per minute.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/jackpot_cards'",
            'BODY=\'{"pool_item_id":8508931}\'',
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.POST $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS -X POST "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG" \\',
            '  -H "Content-Type: application/json" \\',
            '  -d "$BODY"',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const path = '/loot_machine/api/game_platform/v1/jackpot_cards';",
            'const body = JSON.stringify({ pool_item_id: 8508931 });',
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.POST ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'POST',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            "    'Content-Type': 'application/json',",
            '  },',
            '  body,',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/jackpot_cards/{id}': {
    delete: {
      tags: ['Items (S2S)'],
      summary: 'Unmark a jackpot card',
      description:
        'Take a card back out of your jackpot pool. The card returns to ordinary stock: it can be drawn ' +
        'again and it counts towards its pack\'s odds and stock once more. It keeps appearing in ' +
        '`GET /v1/pool_items` throughout — only `is_jackpot_card` flips back to `false`.\n\n' +
        'The path id is the **jackpot card id** (`jackpot_card_id` in the inventory feed), not the ' +
        'pool item id. A card that was already awarded cannot be unmarked — the award is history, and the ' +
        'registry row is its record. Unmarking twice is safe: the second call succeeds and changes nothing. ' +
        'Rate limit: 20 writes per minute.',
      operationId: 'operatorUnmarkJackpotCard',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'The jackpot card id — `jackpot_card_id` from `GET /v1/pool_items`, or `id` from `GET /v1/jackpot_cards`.',
        },
      ],
      responses: {
        '200': {
          description: 'The card is no longer a jackpot card.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'UnmarkJackpotCardResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['jackpot_card'],
                    properties: {
                      jackpot_card: {
                        type: 'object',
                        properties: {
                          id: { type: 'integer', description: 'Jackpot card id.' },
                          name: { type: 'string', description: 'Display name of the card.' },
                          image_url: { type: 'string', format: 'uri', nullable: true, description: 'Card image URL; may be null.' },
                          market_value: { type: 'number', description: 'Fair market value of the card, in USD.' },
                          currency: { type: 'string', description: 'Your account currency for the value.' },
                          status: { type: 'string', enum: ['removed'], description: 'Always `removed` after a successful unmark.' },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  jackpot_card: {
                    id: 90125,
                    name: '2018 Panini Kaboom! Shaquille O\'Neal PSA 9',
                    image_url: 'https://cdn.repackz.fun/rails/active_storage/.../shaq.png',
                    market_value: 9000,
                    currency: 'USD',
                    status: 'removed',
                  },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: '`JACKPOT_CARD_NOT_FOUND` — no jackpot card of yours with that id.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description: '`JACKPOT_CARD_ALREADY_WON` — the card was already awarded to a player and cannot be unmarked.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '429': {
          description: 'Rate limit exceeded — at most 20 jackpot writes per minute.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/jackpot_cards/90125'",
            "BODY='' # DELETE has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.DELETE $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS -X DELETE "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/history': {
    get: {
      tags: ['Transactions (S2S)'],
      summary: 'List transaction history',
      description:
        "Retrieve your wallet transaction ledger for reconciliation, newest first. Every filter is " +
        'optional and they combine together: `round_id` for a single purchase round, `account_id` for one ' +
        "player, and a `start_date`/`end_date` range (apply **both** to use it; the window can span up to " +
        '30 days).\n\n' +
        'Results are paginated. Each entry includes the round it belongs to (`round_id`) and the amount in ' +
        'both minor units (`amount_cents`) and major units (`amount_dollars`).',
      operationId: 'operatorListTransactions',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'round_id',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description: 'Limit results to a single purchase round.',
        },
        {
          name: 'account_id',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description: "The player id from your platform. Limits results to that player's transactions.",
        },
        {
          name: 'start_date',
          in: 'query',
          required: false,
          schema: { type: 'string', format: 'date-time' },
          description:
            'Inclusive lower bound on transaction time. Applied only when both `start_date` and `end_date` ' +
            'are sent. The window may span up to 30 days and `end_date` must be on or after `start_date`, ' +
            'otherwise `INVALID_TIME_INTERVAL`.',
        },
        {
          name: 'end_date',
          in: 'query',
          required: false,
          schema: { type: 'string', format: 'date-time' },
          description: 'Inclusive upper bound on transaction time. Pair it with `start_date`.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1 },
          description: '1-based page number.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Page size. Defaults to 20 and is capped at 200.',
        },
      ],
      responses: {
        '200': {
          description: 'Paginated transaction ledger.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'TransactionListResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['transactions', 'meta'],
                    properties: {
                      transactions: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'Transaction',
                          properties: {
                            transaction_id: { type: 'string', description: 'Your unique transaction reference.' },
                            direction: {
                              type: 'string',
                              enum: ['debit', 'credit', 'refund'],
                              description: 'Direction of the transaction.',
                            },
                            amount_cents: { type: 'integer', description: 'Amount in minor units (cents).' },
                            status: {
                              type: 'string',
                              enum: ['pending', 'successful', 'failed'],
                              description: 'Current status of the transaction.',
                            },
                            created_at: { type: 'string', format: 'date-time', description: 'When the transaction was created (ISO 8601).' },
                            round_id: {
                              type: 'string',
                              nullable: true,
                              description: 'The purchase round this transaction belongs to; null if none.',
                            },
                            amount_dollars: { type: 'number', description: 'Amount in major currency units.' },
                          },
                        },
                      },
                      meta: {
                        type: 'object',
                        title: 'TransactionListMeta',
                        required: ['page', 'total_count'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          total_count: { type: 'integer', description: 'Total matching transactions across all pages.' },
                        },
                        description: 'Pagination details for this list.',
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  transactions: [
                    {
                      transaction_id: 'dg-tx-8841',
                      direction: 'debit',
                      amount_cents: 1999,
                      status: 'successful',
                      created_at: '2026-05-28T14:02:11Z',
                      round_id: 'round-7731',
                      amount_dollars: 19.99,
                    },
                  ],
                  meta: { page: 1, total_count: 1 },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description: '`INVALID_TIME_INTERVAL` — the date window exceeds 30 days, `end_date` is before `start_date`, or a date could not be parsed.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "path = '/loot_machine/api/game_platform/v1/history?account_id=player-7788&page=1'",
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}" # sign the path including its query string',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const path = '/loot_machine/api/game_platform/v1/history?account_id=player-7788&page=1';",
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`; // sign the path including its query string',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/history?account_id=player-7788&page=1'",
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY" # sign the path including its query string',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/rounds/{round_id}': {
    get: {
      tags: ['Transactions (S2S)'],
      summary: 'Get a round',
      description:
        'Retrieve one purchase round in full: its status, every transaction recorded under its `round_id`, ' +
        'and the cards the purchase revealed. Use it to see which cards a player got, or to investigate a ' +
        'disputed purchase.\n\n' +
        'One purchase is one round, so a multi-pack purchase reveals several cards. The `round_id` arrives on ' +
        'the `/debit` callback, but the cards are only drawn after your debit succeeds, so call this endpoint ' +
        'once the purchase has completed.\n\n' +
        'Optional card fields are omitted when they have no value rather than returned as null.',
      operationId: 'operatorGetRound',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'round_id',
          in: 'path',
          required: true,
          schema: { type: 'string' },
          description: 'The round id from the `/debit` callback, e.g. `rnd_40565`.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      responses: {
        '200': {
          description: 'The round with its transactions and revealed cards.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'RoundResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['round_id', 'account_id', 'status', 'opened_at', 'transactions', 'items_revealed'],
                    properties: {
                      round_id: { type: 'string', description: 'The round id.' },
                      account_id: { type: 'string', description: 'The player id from your platform.' },
                      game_id: { type: 'string', nullable: true, description: 'The pack (game) the round was played on.' },
                      game_name: { type: 'string', nullable: true, description: 'Display name of the pack.' },
                      game_type: {
                        type: 'string',
                        enum: ['loot_pack', 'custom_pack'],
                        description: 'Game format: `loot_pack` for a regular pack, `custom_pack` for a Custom Pack Lab round.',
                      },
                      status: {
                        type: 'string',
                        enum: ['revealed', 'pending', 'failed', 'refunded', 'unknown'],
                        description:
                          '`revealed` — the purchase completed and its cards were drawn. `pending` — a transaction ' +
                          'is still in progress. `failed` — a transaction failed. `refunded` — the purchase was ' +
                          'refunded. `unknown` — none of the above could be determined.',
                      },
                      opened_at: { type: 'string', format: 'date-time', description: 'When the round started (ISO 8601).' },
                      transactions: {
                        type: 'array',
                        description: 'Every transaction recorded under this round, oldest first.',
                        items: {
                          type: 'object',
                          title: 'RoundTransaction',
                          properties: {
                            transaction_id: {
                              type: 'string',
                              description: 'The transaction id sent on the callback, e.g. `dbt_40565`, `crd_90231`.',
                            },
                            direction: {
                              type: 'string',
                              enum: ['debit', 'credit', 'refund', 'shipping_settle'],
                              description: 'Callback type of the transaction.',
                            },
                            amount_cents: { type: 'integer', description: 'Amount in minor units (cents).' },
                            status: {
                              type: 'string',
                              enum: ['pending', 'successful', 'failed'],
                              description: 'Current status of the transaction.',
                            },
                            created_at: { type: 'string', format: 'date-time', description: 'When the transaction was created (ISO 8601).' },
                          },
                        },
                      },
                      items_revealed: {
                        type: 'array',
                        description: 'The cards revealed by this purchase. Empty if the purchase never completed.',
                        items: {
                          type: 'object',
                          title: 'RoundCard',
                          properties: {
                            id: {
                              type: 'integer',
                              description:
                                'Unique id of the card — the `pool_item_id` used in `crd_<pool_item_id>` and on `/shipping-debit`.',
                            },
                            name: { type: 'string', nullable: true, description: 'Display name of the card. Omitted when not set.' },
                            rarity: { type: 'string', nullable: true, description: 'Rarity tier of the card. Omitted when not set.' },
                            image_url: { type: 'string', nullable: true, description: 'Artwork URL for the card. Omitted when not set.' },
                            fair_market_value: {
                              type: 'number',
                              nullable: true,
                              description: 'Current fair market value of the card. Omitted when not set.',
                            },
                            card_details: { $ref: '#/components/schemas/CardDetails' },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  round_id: 'rnd_40565',
                  account_id: 'player-7788',
                  game_id: '512',
                  game_name: 'Pokémon Starter Pack',
                  game_type: 'loot_pack',
                  status: 'revealed',
                  opened_at: '2026-05-28T14:02:11Z',
                  transactions: [
                    {
                      transaction_id: 'dbt_40565',
                      direction: 'debit',
                      amount_cents: 3000,
                      status: 'successful',
                      created_at: '2026-05-28T14:02:11Z',
                    },
                    {
                      transaction_id: 'crd_90231',
                      direction: 'credit',
                      amount_cents: 900,
                      status: 'successful',
                      created_at: '2026-05-28T14:03:40Z',
                    },
                  ],
                  items_revealed: [
                    {
                      id: 90231,
                      name: 'Charizard /99',
                      rarity: 'legendary',
                      image_url: 'https://cdn.repackz.fun/items/charizard.png',
                      fair_market_value: 10.0,
                    },
                  ],
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: '`NOT_FOUND` — no transactions exist for this `round_id` on your platform.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "round_id = 'rnd_40565'",
            'path = "/loot_machine/api/game_platform/v1/rounds/#{round_id}"',
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const roundId = 'rnd_40565';",
            'const path = `/loot_machine/api/game_platform/v1/rounds/${roundId}`;',
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "ROUND_ID='rnd_40565'",
            'PATH_URI="/loot_machine/api/game_platform/v1/rounds/$ROUND_ID"',
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/unfinished_transactions': {
    get: {
      tags: ['Transactions (S2S)'],
      summary: 'List unfinished transactions',
      description:
        'List the transactions that have not settled yet, newest first — use it to investigate a player ' +
        'complaint ("I was charged but…", "my sale was not paid") or to reconcile in-flight money. A ' +
        'transaction is unfinished when it is:\n\n' +
        '- `pending` — still in flight; we are delivering or retrying it.\n' +
        '- `failed` **and** a `credit` or `refund` — money owed to the player that we could not deliver after ' +
        'all retries.\n\n' +
        'A `start_date`/`end_date` range is **required** and may span up to 30 days. Results are paginated.',
      operationId: 'operatorListUnfinishedTransactions',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'start_date',
          in: 'query',
          required: true,
          schema: { type: 'string', format: 'date-time' },
          description:
            'Inclusive lower bound on transaction creation time. The window may span up to 30 days and ' +
            '`end_date` must be on or after `start_date`, otherwise `INVALID_TIME_INTERVAL`.',
        },
        {
          name: 'end_date',
          in: 'query',
          required: true,
          schema: { type: 'string', format: 'date-time' },
          description: 'Inclusive upper bound on transaction creation time.',
        },
        {
          name: 'account_id',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description: "The player id from your platform. Limits results to that player's transactions.",
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1 },
          description: '1-based page number.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Page size. Defaults to 20 and is capped at 200.',
        },
      ],
      responses: {
        '200': {
          description: 'Paginated list of unfinished transactions.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'UnfinishedTransactionListResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['transactions', 'meta'],
                    properties: {
                      transactions: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'UnfinishedTransaction',
                          properties: {
                            transaction_id: {
                              type: 'string',
                              description: 'The transaction id sent on the callback, e.g. `dbt_40565`, `crd_90231`.',
                            },
                            direction: {
                              type: 'string',
                              enum: ['debit', 'credit', 'refund', 'shipping_debit', 'shipping_settle'],
                              description: 'Callback type of the transaction.',
                            },
                            amount_cents: { type: 'integer', description: 'Amount in minor units (cents).' },
                            status: {
                              type: 'string',
                              enum: ['pending', 'failed'],
                              description: '`pending` — still in flight. `failed` — a credit or refund we could not deliver.',
                            },
                            created_at: { type: 'string', format: 'date-time', description: 'When the transaction was created (ISO 8601).' },
                            updated_at: { type: 'string', format: 'date-time', description: 'When the transaction last changed, e.g. its latest delivery attempt (ISO 8601).' },
                            round_id: {
                              type: 'string',
                              nullable: true,
                              description:
                                'The purchase round this transaction belongs to. A `shipping_debit` belongs to no round — ' +
                                'it repeats its own `ship_<shipping_id>` transaction id here.',
                            },
                            amount_dollars: { type: 'number', description: 'Amount in major currency units.' },
                            account_id: { type: 'string', nullable: true, description: 'The player id from your platform.' },
                          },
                        },
                      },
                      meta: {
                        type: 'object',
                        title: 'UnfinishedTransactionListMeta',
                        required: ['page', 'total_count'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          total_count: { type: 'integer', description: 'Total matching transactions across all pages.' },
                        },
                        description: 'Pagination details for this list.',
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  transactions: [
                    {
                      transaction_id: 'crd_90231',
                      direction: 'credit',
                      amount_cents: 900,
                      status: 'pending',
                      created_at: '2026-05-28T14:03:40Z',
                      updated_at: '2026-05-28T14:34:02Z',
                      round_id: 'rnd_40565',
                      amount_dollars: 9.0,
                      account_id: 'player-7788',
                    },
                  ],
                  meta: { page: 1, total_count: 1 },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            '`INVALID_TIME_INTERVAL` — `start_date` or `end_date` is missing, the window exceeds 30 days, ' +
            '`end_date` is before `start_date`, or a date could not be parsed.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "path = '/loot_machine/api/game_platform/v1/unfinished_transactions?start_date=2026-05-01T00:00:00Z&end_date=2026-05-30T00:00:00Z'",
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}" # sign the path including its query string',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const path = '/loot_machine/api/game_platform/v1/unfinished_transactions?start_date=2026-05-01T00:00:00Z&end_date=2026-05-30T00:00:00Z';",
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`; // sign the path including its query string',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/unfinished_transactions?start_date=2026-05-01T00:00:00Z&end_date=2026-05-30T00:00:00Z'",
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY" # sign the path including its query string',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/players/{account_id}/inventory': {
    get: {
      tags: ['Players (S2S)'],
      summary: "List a player's inventory",
      description:
        "List the cards a player currently owns. Cards the player has sold back are excluded. Results are " +
        'newest first and paginated.\n\n' +
        'Optional fields are omitted from each card when they have no value rather than returned as null. ' +
        'An unknown player simply returns an empty list with a `total_count` of 0, not an error.',
      operationId: 'operatorListPlayerInventory',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'account_id',
          in: 'path',
          required: true,
          schema: { type: 'string' },
          description: 'The player id from your platform whose inventory you want.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1 },
          description: '1-based page number.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Page size. Defaults to 20 and is capped at 200.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      responses: {
        '200': {
          description: "Paginated list of the player's owned cards.",
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'InventoryResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['items', 'meta'],
                    properties: {
                      items: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'InventoryCard',
                          properties: {
                            id: { type: 'integer', description: 'Unique id of the card.' },
                            name: { type: 'string', nullable: true, description: 'Display name of the card. Omitted when not set.' },
                            rarity: { type: 'string', nullable: true, description: 'Rarity tier of the card. Omitted when not set.' },
                            image_url: { type: 'string', nullable: true, description: 'Artwork URL for the card. Omitted when not set.' },
                            fair_market_value: {
                              type: 'number',
                              nullable: true,
                              description: 'Current fair market value of the card. Omitted when not set.',
                            },
                            buy_back_value: {
                              type: 'number',
                              nullable: true,
                              description: 'Amount the player would receive if they sell the card back. Omitted when not set.',
                            },
                            engagement_status: {
                              type: 'string',
                              enum: ['unavailable', 'listed', 'on_hands', 'redeemed', 'buyback', 'burned', 'reserved'],
                              description: 'Current state of the card in the player\'s inventory.',
                            },
                            card_details: { $ref: '#/components/schemas/CardDetails' },
                          },
                        },
                      },
                      meta: {
                        type: 'object',
                        title: 'InventoryMeta',
                        required: ['page', 'total_count'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          total_count: { type: 'integer', description: 'Total owned cards across all pages.' },
                        },
                        description: 'Pagination details for this list.',
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  items: [
                    {
                      id: 90231,
                      name: 'Conor McGregor /99',
                      rarity: 'legendary',
                      image_url: 'https://cdn.repackz.fun/items/conor.png',
                      fair_market_value: 42.5,
                      buy_back_value: 38.25,
                      engagement_status: 'on_hands',
                    },
                  ],
                  meta: { page: 1, total_count: 1 },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "account_id = 'player-7788'",
            'path = "/loot_machine/api/game_platform/v1/players/#{account_id}/inventory?page=1"',
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const accountId = 'player-7788';",
            'const path = `/loot_machine/api/game_platform/v1/players/${accountId}/inventory?page=1`;',
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "ACCOUNT_ID='player-7788'",
            'PATH_URI="/loot_machine/api/game_platform/v1/players/$ACCOUNT_ID/inventory?page=1"',
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/players/{account_id}/vault': {
    get: {
      tags: ['Players (S2S)'],
      summary: "Get a player's vault history",
      description:
        'Every card the player has pulled, with when they bought it and where it is now: still in their vault, ' +
        'on its way, shipped or delivered, or sold back. Results are newest purchase first and paginated.\n\n' +
        '- **Shipped and delivered cards** carry the delivery with its carrier, tracking number and a link to the ' +
        "carrier's tracking page. Read the full delivery with **Get one delivery** using `shipping.id`.\n" +
        '- **Sold cards** carry what the player received — the amount and `reason` of the card\'s `/credit` ' +
        '(also when that callback was not sent to you), or on Stripe funding the payout to the player\'s ' +
        'balance in USD. A card bought back from a canceled delivery, or because we could not ship it, also ' +
        'keeps that delivery.\n\n' +
        'Unlike **List a player\'s inventory**, which lists only what the player holds now, this endpoint keeps ' +
        'shipped and sold cards. An unknown player returns an empty list with a `total_count` of 0.',
      operationId: 'operatorGetPlayerVault',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'account_id',
          in: 'path',
          required: true,
          schema: { type: 'string' },
          description: 'The player id from your platform whose vault you want.',
        },
        {
          name: 'status',
          in: 'query',
          required: false,
          schema: { type: 'string', example: 'shipped,delivered' },
          description:
            'Only cards in these states, comma-separated: `on_hands`, `in_delivery`, `shipped`, `delivered`, ' +
            '`sold_back`. Any other value returns `INVALID_STATUS`.',
        },
        {
          name: 'from',
          in: 'query',
          required: false,
          schema: { type: 'string', format: 'date-time' },
          description: 'Only cards bought at or after this time (ISO 8601).',
        },
        {
          name: 'to',
          in: 'query',
          required: false,
          schema: { type: 'string', format: 'date-time' },
          description: 'Only cards bought at or before this time (ISO 8601).',
        },
        {
          name: 'game_id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Only cards pulled from this game.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1 },
          description: '1-based page number.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Page size. Defaults to 20 and is capped at 200.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      responses: {
        '200': {
          description: "Paginated vault history of the player's cards.",
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'VaultResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['account_id', 'items', 'meta'],
                    properties: {
                      account_id: { type: 'string', description: 'The player id you asked for.' },
                      items: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'VaultCard',
                          required: ['id', 'game_id', 'round_id', 'purchased_at', 'status'],
                          properties: {
                            id: { type: 'integer', description: 'Unique id of the card.' },
                            name: { type: 'string', nullable: true, description: 'Display name of the card.' },
                            image_url: { type: 'string', nullable: true, description: 'Artwork URL for the card.' },
                            fair_market_value: {
                              type: 'number',
                              nullable: true,
                              description: 'Current fair market value of the card in USD.',
                            },
                            game_id: { type: 'integer', description: 'The game (pack) the card was pulled from.' },
                            game_name: {
                              type: 'string',
                              nullable: true,
                              description: 'Name of that game as **List available games** shows it, your rename included.',
                            },
                            round_id: { type: 'string', description: 'The round the card was pulled in — the `round_id` of its `/debit`.' },
                            purchased_at: { type: 'string', format: 'date-time', description: 'When the player bought the pack.' },
                            opened_at: {
                              type: 'string',
                              format: 'date-time',
                              nullable: true,
                              description: 'When the player opened the pack. `null` until they do.',
                            },
                            status: {
                              type: 'string',
                              enum: ['on_hands', 'in_delivery', 'shipped', 'delivered', 'sold_back'],
                              description:
                                '`on_hands` — in the player\'s vault. `in_delivery` — the player ordered a delivery ' +
                                'and it has not shipped yet. `shipped` — on its way. `delivered` — the carrier ' +
                                'reported delivery. `sold_back` — the player sold it back, or it was bought back from ' +
                                'a delivery.',
                            },
                            sold: {
                              type: 'object',
                              title: 'VaultSale',
                              nullable: true,
                              description: 'Set only when `status` is `sold_back`.',
                              properties: {
                                at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card was sold back.' },
                                amount_cents: {
                                  type: 'integer',
                                  nullable: true,
                                  description:
                                    'What the player received, in cents of `currency`: the amount of the card\'s `/credit`, ' +
                                    'or on Stripe funding the payout to the player\'s balance (USD).',
                                },
                                currency: { type: 'string', nullable: true, description: 'Currency of `amount_cents`.' },
                                reason: {
                                  type: 'string',
                                  enum: ['sale', 'auto_sale', 'unavailable_item', 'shipping_canceled'],
                                  description:
                                    '`sale` — the player sold it. `auto_sale` — sold automatically after the hold ' +
                                    'window. `unavailable_item` — we could not ship it and compensated the player. ' +
                                    '`shipping_canceled` — the player canceled the delivery it was in.',
                                },
                              },
                            },
                            shipping: {
                              type: 'object',
                              title: 'VaultShipping',
                              nullable: true,
                              description:
                                'The delivery the card is in or travelled in. Carrier, tracking and dates are set once ' +
                                'the card shipped.',
                              properties: {
                                id: { type: 'integer', description: 'Delivery id — use it with **Get one delivery**.' },
                                status: {
                                  type: 'string',
                                  description: 'Status of the whole delivery (it may carry other cards too).',
                                },
                                shipping_type: { type: 'string', enum: ['delivery', 'smc_transfer'] },
                                carrier: { type: 'string', nullable: true, example: 'USPS' },
                                tracking_number: { type: 'string', nullable: true },
                                tracking_url: {
                                  type: 'string',
                                  nullable: true,
                                  description: "The carrier's public tracking page. `null` for a carrier we have no link for.",
                                },
                                shipped_at: { type: 'string', format: 'date-time', nullable: true },
                                delivered_at: { type: 'string', format: 'date-time', nullable: true },
                              },
                            },
                            card_details: { $ref: '#/components/schemas/CardDetails' },
                          },
                        },
                      },
                      meta: {
                        type: 'object',
                        title: 'VaultMeta',
                        required: ['page', 'per', 'total_count'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          per: { type: 'integer', description: 'Page size used.' },
                          total_count: { type: 'integer', description: 'Total matching cards across all pages.' },
                        },
                        description: 'Pagination details for this list.',
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  account_id: 'player-7788',
                  items: [
                    {
                      id: 90231,
                      name: '2023 Panini Prizm Conor McGregor /99 PSA 10',
                      image_url: 'https://cdn.repackz.fun/items/conor.png',
                      fair_market_value: 42.5,
                      game_id: 41,
                      game_name: 'UFC Hits',
                      round_id: 'rnd_551204',
                      purchased_at: '2026-10-08T17:02:11Z',
                      opened_at: '2026-10-08T17:02:40Z',
                      status: 'shipped',
                      sold: null,
                      shipping: {
                        id: 8812,
                        status: 'shipped',
                        shipping_type: 'delivery',
                        carrier: 'USPS',
                        tracking_number: '9400111899223197428490',
                        tracking_url: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=9400111899223197428490',
                        shipped_at: '2026-10-12T14:20:00Z',
                        delivered_at: null,
                      },
                    },
                    {
                      id: 90107,
                      name: '2022 Topps Chrome Jon Jones Refractor',
                      image_url: 'https://cdn.repackz.fun/items/jones.png',
                      fair_market_value: 8.0,
                      game_id: 41,
                      game_name: 'UFC Hits',
                      round_id: 'rnd_551190',
                      purchased_at: '2026-10-08T16:55:03Z',
                      opened_at: '2026-10-08T16:55:30Z',
                      status: 'sold_back',
                      sold: { at: '2026-10-08T16:56:02Z', amount_cents: 720, currency: 'USD', reason: 'sale' },
                      shipping: null,
                    },
                  ],
                  meta: { page: 1, per: 20, total_count: 2 },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            '`INVALID_STATUS` — an unknown `status` value; `INVALID_GAME_ID` — `game_id` is not a number; ' +
            '`INVALID_TIME_INTERVAL` — `from`/`to` could not be parsed or `to` is before `from`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "account_id = 'player-7788'",
            'path = "/loot_machine/api/game_platform/v1/players/#{account_id}/vault?status=shipped,delivered&page=1"',
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const accountId = 'player-7788';",
            'const path = `/loot_machine/api/game_platform/v1/players/${accountId}/vault?status=shipped,delivered&page=1`;',
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "ACCOUNT_ID='player-7788'",
            'PATH_URI="/loot_machine/api/game_platform/v1/players/$ACCOUNT_ID/vault?status=shipped,delivered&page=1"',
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/players/{account_id}/summary': {
    get: {
      tags: ['Players (S2S)'],
      summary: "Get a player's spend summary",
      description:
        "Get spend totals for one player over a date range — useful for responsible-gaming limits and " +
        'reporting. `from` and `to` are **required** and the window can span up to 90 days.\n\n' +
        'All `*_cents` values are in your account currency minor units (cents). `num_purchases` counts ' +
        'successful purchases, `total_spent_cents` sums what the player spent, `total_returned_cents` sums ' +
        'credits and refunds, and `net_cents` is spent minus returned.',
      operationId: 'operatorGetPlayerSummary',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'account_id',
          in: 'path',
          required: true,
          schema: { type: 'string' },
          description: 'The player id from your platform.',
        },
        {
          name: 'from',
          in: 'query',
          required: true,
          schema: { type: 'string', format: 'date-time' },
          description: 'Start of the period (required). The window (`to` − `from`) may span up to 90 days and `to` must be on or after `from`.',
        },
        {
          name: 'to',
          in: 'query',
          required: true,
          schema: { type: 'string', format: 'date-time' },
          description: 'End of the period (required). An invalid range returns `INVALID_TIME_INTERVAL`.',
        },
      ],
      responses: {
        '200': {
          description: 'Spend summary for the player over the requested window.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'PlayerSummaryResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    title: 'PlayerSummary',
                    required: [
                      'account_id',
                      'from',
                      'to',
                      'num_purchases',
                      'total_spent_cents',
                      'total_returned_cents',
                      'net_cents',
                      'currency',
                    ],
                    properties: {
                      account_id: { type: 'string', description: 'The player id you requested.' },
                      from: { type: 'string', format: 'date-time', description: 'Start of the period (ISO 8601).' },
                      to: { type: 'string', format: 'date-time', description: 'End of the period (ISO 8601).' },
                      num_purchases: { type: 'integer', description: 'Number of successful purchases in the window.' },
                      total_spent_cents: { type: 'integer', description: 'Total spent in the window, in minor units (cents).' },
                      total_returned_cents: {
                        type: 'integer',
                        description: 'Total returned via credits and refunds in the window, in minor units (cents).',
                      },
                      net_cents: { type: 'integer', description: 'Net spend (spent minus returned), in minor units (cents).' },
                      currency: { type: 'string', description: 'Your account currency.' },
                    },
                  },
                },
              },
              example: {
                result: {
                  account_id: 'player-7788',
                  from: '2026-05-01T00:00:00Z',
                  to: '2026-05-29T00:00:00Z',
                  num_purchases: 12,
                  total_spent_cents: 23988,
                  total_returned_cents: 4500,
                  net_cents: 19488,
                  currency: 'USD',
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description: '`INVALID_TIME_INTERVAL` — `from`/`to` is missing, `to` is before `from`, the window exceeds 90 days, or a date could not be parsed.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "account_id = 'player-7788'",
            'path = "/loot_machine/api/game_platform/v1/players/#{account_id}/summary?from=2026-05-01T00:00:00Z&to=2026-05-29T00:00:00Z"',
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const accountId = 'player-7788';",
            'const path = `/loot_machine/api/game_platform/v1/players/${accountId}/summary?from=2026-05-01T00:00:00Z&to=2026-05-29T00:00:00Z`;',
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "ACCOUNT_ID='player-7788'",
            'PATH_URI="/loot_machine/api/game_platform/v1/players/$ACCOUNT_ID/summary?from=2026-05-01T00:00:00Z&to=2026-05-29T00:00:00Z"',
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/players/{account_id}/status': {
    get: {
      tags: ['Players (S2S)'],
      summary: "Get a player's status",
      description:
        "Get a quick snapshot of a player — handy for self-exclusion or account-closure checks (for " +
        'example, block a close while a purchase round is still in progress or the player still holds ' +
        'cards of value).\n\n' +
        '`inventory_value_cents` is the total fair market value of the cards the player owns, in minor ' +
        'units (cents). An unknown player returns `has_pending_orders:false`, `inventory_value_cents:0`, ' +
        'and `last_activity_at:null`, not an error.',
      operationId: 'operatorGetPlayerStatus',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'account_id',
          in: 'path',
          required: true,
          schema: { type: 'string' },
          description: 'The player id from your platform.',
        },
      ],
      responses: {
        '200': {
          description: 'Player status snapshot.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'PlayerStatusResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    title: 'PlayerStatus',
                    required: ['account_id', 'has_pending_orders', 'inventory_value_cents', 'last_activity_at'],
                    properties: {
                      account_id: { type: 'string', description: 'The player id you requested.' },
                      has_pending_orders: {
                        type: 'boolean',
                        description: 'True if the player has a purchase round still in progress.',
                      },
                      inventory_value_cents: {
                        type: 'integer',
                        description: 'Total fair market value of the cards the player owns, in minor units (cents).',
                      },
                      last_activity_at: {
                        type: 'string',
                        format: 'date-time',
                        nullable: true,
                        description: 'When the player was last active (ISO 8601); null if they have never played.',
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  account_id: 'player-7788',
                  has_pending_orders: false,
                  inventory_value_cents: 128750,
                  last_activity_at: '2026-05-29T09:14:03Z',
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "account_id = 'player-7788'",
            'path = "/loot_machine/api/game_platform/v1/players/#{account_id}/status"',
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const accountId = 'player-7788';",
            'const path = `/loot_machine/api/game_platform/v1/players/${accountId}/status`;',
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "ACCOUNT_ID='player-7788'",
            'PATH_URI="/loot_machine/api/game_platform/v1/players/$ACCOUNT_ID/status"',
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/players/{account_id}/shippings': {
    get: {
      tags: ['Deliveries (S2S)'],
      summary: "List a player's deliveries",
      description:
        "List a player's physical-card deliveries, newest first and paginated — useful for support " +
        'tooling and for deciding whether a delivery can still be canceled.\n\n' +
        'Each delivery carries its `status`, the per-card `settle_status` list, the `charge_breakdown` ' +
        'the player paid (null until charged), and the `is_allow_to_cancel` flag — **true only while the ' +
        'delivery is `paid` with unresolved cards** and fulfillment has not started; use it to gate the cancel call.\n\n' +
        'An unknown player simply returns an empty list with a `total_count` of 0, not an error.',
      operationId: 'operatorListPlayerShippings',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'account_id',
          in: 'path',
          required: true,
          schema: { type: 'string' },
          description: 'The player id from your platform whose deliveries you want.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1 },
          description: '1-based page number.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Page size. Defaults to 20 and is capped at 200.',
        },
      ],
      responses: {
        '200': {
          description: "Paginated list of the player's deliveries.",
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'PlayerShippingsResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['account_id', 'shippings', 'meta'],
                    properties: {
                      account_id: { type: 'string', description: 'The player id you requested.' },
                      shippings: { type: 'array', items: S2S_SHIPPING_SCHEMA },
                      meta: {
                        type: 'object',
                        title: 'PlayerShippingsMeta',
                        required: ['page', 'total_count'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          total_count: { type: 'integer', description: 'Total deliveries across all pages.' },
                        },
                        description: 'Pagination details for this list.',
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  account_id: 'player-7788',
                  shippings: [S2S_SHIPPING_EXAMPLE],
                  meta: { page: 1, total_count: 1 },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "account_id = 'player-7788'",
            'path = "/loot_machine/api/game_platform/v1/players/#{account_id}/shippings?page=1"',
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const accountId = 'player-7788';",
            'const path = `/loot_machine/api/game_platform/v1/players/${accountId}/shippings?page=1`;',
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "ACCOUNT_ID='player-7788'",
            'PATH_URI="/loot_machine/api/game_platform/v1/players/$ACCOUNT_ID/shippings?page=1"',
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/pool_shippings': {
    get: {
      tags: ['Deliveries (S2S)'],
      summary: 'List all your deliveries',
      description:
        'ALL deliveries your players ordered, newest first — the machine twin of the Console → Deliveries ' +
        'page: same rows, same filters. Use it to sync your own books instead of walking players one by ' +
        'one; `GET /pool_shippings/{id}` returns the full detail of one row.\n\n' +
        '**Filters** are exact matches on indexed columns and combine with AND: `status`, `shipping_type`, ' +
        '`tracking_number`, `id`, `certificate_number` (which delivery did this card travel in), `user_id`, ' +
        '`account_id` (the player id from YOUR platform), and `created_from` / `created_to` (bare dates, ' +
        'whole days inclusive). Filtering by player email is console-only and not available over the API.\n\n' +
        'Only outbound deliveries are listed, and merged shipments are hidden (a merged delivery reads as a ' +
        'duplicate that never arrives). Money figures come from the checkout snapshot in your currency; our ' +
        'internal notes are never included.',
      operationId: 'operatorListShippings',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'status',
          in: 'query',
          required: false,
          schema: {
            type: 'string',
            enum: ['pending', 'waiting_for_payment', 'paid', 'request_shipping', 'shipped', 'delivered', 'canceled'],
          },
          description: 'Exact delivery status.',
        },
        {
          name: 'shipping_type',
          in: 'query',
          required: false,
          schema: { type: 'string', enum: ['delivery', 'smc_transfer'] },
          description: 'Postal shipment vs Ship My Cards vault transfer.',
        },
        {
          name: 'tracking_number',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description: 'Exact carrier tracking number.',
        },
        {
          name: 'id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'One delivery by id (still scoped to your account — a foreign id answers an empty list).',
        },
        {
          name: 'certificate_number',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description: 'Find the delivery carrying the card with this grading certificate number.',
        },
        {
          name: 'user_id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Our internal player id, as returned in each row.',
        },
        {
          name: 'account_id',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description:
            'The player id from YOUR platform (the same id you launch sessions with). Resolved across all of ' +
            "the player's sessions, including expired ones.",
        },
        {
          name: 'created_from',
          in: 'query',
          required: false,
          schema: { type: 'string', format: 'date', example: '2026-06-01' },
          description: 'Deliveries created on/after this date (whole day, server time). Unparsable dates are ignored.',
        },
        {
          name: 'created_to',
          in: 'query',
          required: false,
          schema: { type: 'string', format: 'date', example: '2026-06-30' },
          description: 'Deliveries created on/before this date — the whole day is included.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1 },
          description: '1-based page number.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Page size. Defaults to 20 and is capped at 200.',
        },
      ],
      responses: {
        '200': {
          description: 'Paginated list of your deliveries, newest first.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'OperatorShippingsResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['pool_shippings', 'meta'],
                    properties: {
                      pool_shippings: { type: 'array', items: S2S_DELIVERY_ROW_SCHEMA },
                      meta: {
                        type: 'object',
                        title: 'OperatorShippingsMeta',
                        required: ['page', 'per', 'total_count'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          per: { type: 'integer', description: 'Page size used.' },
                          total_count: { type: 'integer', description: 'Total deliveries across all pages.' },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  pool_shippings: [S2S_DELIVERY_ROW_EXAMPLE],
                  meta: { page: 1, per: 20, total_count: 1 },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "# Filters go into the query string — it is part of the signed path.",
            "path = '/loot_machine/api/game_platform/v1/pool_shippings?status=shipped&created_from=2026-06-01&page=1'",
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            '// Filters go into the query string — it is part of the signed path.',
            "const path = '/loot_machine/api/game_platform/v1/pool_shippings?status=shipped&created_from=2026-06-01&page=1';",
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            '# Filters go into the query string — it is part of the signed path.',
            'PATH_URI="/loot_machine/api/game_platform/v1/pool_shippings?status=shipped&created_from=2026-06-01&page=1"',
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/pool_shippings/{id}': {
    get: {
      tags: ['Deliveries (S2S)'],
      summary: 'Get one delivery',
      description:
        'One delivery in full — exactly what the Console → Deliveries detail page shows: the full postal ' +
        'address, the charge breakdown and protection snapshot the player accepted at checkout, every card ' +
        'with its locked FMV / round / pack / per-card fulfillment status / owner (yours vs shared) / ' +
        'refund, and the tracking pair once the parcel ships.\n\n' +
        'A `404` means the id is not one of your outbound deliveries (inbound warehouse intake and merged ' +
        'shipments also answer 404).',
      operationId: 'operatorGetShipping',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Delivery (pool_shipping) id, e.g. from GET /pool_shippings.',
        },
      ],
      responses: {
        '200': {
          description: 'The delivery with its address, charge, protection and cards.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'OperatorShippingResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['pool_shipping'],
                    properties: { pool_shipping: S2S_DELIVERY_DETAIL_SCHEMA },
                  },
                },
              },
              example: { result: { pool_shipping: S2S_DELIVERY_DETAIL_EXAMPLE } },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: '`NOT_FOUND` (id 4040) — no outbound delivery with that id belongs to your account.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            'shipping_id = 4012',
            'path = "/loot_machine/api/game_platform/v1/pool_shippings/#{shipping_id}"',
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            'const shippingId = 4012;',
            'const path = `/loot_machine/api/game_platform/v1/pool_shippings/${shippingId}`;',
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            'SHIPPING_ID=4012',
            'PATH_URI="/loot_machine/api/game_platform/v1/pool_shippings/$SHIPPING_ID"',
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/pool_shippings/{id}/cancel': {
    post: {
      tags: ['Deliveries (S2S)'],
      summary: 'Cancel a delivery',
      description:
        'Cancel a **`paid`** delivery from your backend before fulfillment starts — for example on a ' +
        'support request or an account closure.\n\n' +
        'The delivery is fully unwound: every card is bought back at the regular buyback price ' +
        '(`fair_market_value × buyback_percent` — a `credit` wallet callback per card) and the shipping + ' +
        'handling charge is refunded to the wallet (`refund` callback). The cards leave the player as ' +
        'sold; the delivery becomes `canceled` with every card `settle_status: canceled`.\n\n' +
        '**Idempotent:** canceling an already-canceled delivery returns `200` with the current state — no ' +
        'double credit or refund.\n\n' +
        'The window closes once fulfillment starts: from `request_shipping` onward the call returns ' +
        '`422 SHIPPING_NOT_CANCELABLE`. Gate on the `is_allow_to_cancel` flag from the per-player list. ' +
        'A `422 SHIPPING_CANCEL_FAILED` is transient — safe to retry by calling again.',
      operationId: 'operatorCancelShipping',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Delivery (pool_shipping) id, e.g. from the per-player shippings list. Must belong to one of your players.',
        },
      ],
      responses: {
        '200': {
          description: 'The canceled delivery (or its current state when it was already canceled).',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'CancelShippingResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['shipping'],
                    properties: { shipping: S2S_SHIPPING_SCHEMA },
                  },
                },
              },
              example: { result: { shipping: S2S_SHIPPING_CANCELED_EXAMPLE } },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: '`NOT_FOUND` (id 4040) — no delivery with that id belongs to your account.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            '`SHIPPING_NOT_CANCELABLE` (id 2098) — the cancel window is closed: fulfillment already started ' +
            '(`request_shipping` or later). Do NOT retry. `SHIPPING_CANCEL_FAILED` (id 2097) — the cancel did ' +
            'not go through; transient, safe to retry with another call.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            'shipping_id = 4012',
            'path = "/loot_machine/api/game_platform/v1/pool_shippings/#{shipping_id}/cancel"',
            "body = '' # POST with no body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.POST #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Post.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            'const shippingId = 4012;',
            'const path = `/loot_machine/api/game_platform/v1/pool_shippings/${shippingId}/cancel`;',
            "const body = ''; // POST with no body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.POST ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'POST',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            'SHIPPING_ID=4012',
            'PATH_URI="/loot_machine/api/game_platform/v1/pool_shippings/$SHIPPING_ID/cancel"',
            "BODY='' # POST with no body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.POST $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS -X POST "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },

  '/loot_machine/api/game_platform/v1/pool_items': {
    get: {
      tags: ['Items (S2S)'],
      summary: 'List your own inventory',
      description:
        'Return the cards **you** loaded into the platform under your own name — your stock, for ' +
        'reconciliation against your books. Shared platform inventory is never returned here, even when ' +
        'it sits in the same packs; for what your players can pull, use `GET /v1/items`.\n\n' +
        '**`pool_offer_id`** narrows the dump to the stock currently sitting in those packs ("which of ' +
        'my cards is live in pack X"). An id that reaches nothing — unknown, or a pack belonging to ' +
        'another operator — is **not an error**: you get `200` with an empty `pool_items` array, exactly ' +
        'the same answer an id that simply holds none of your stock gets. That is deliberate, so the ' +
        'filter can never be used to probe which packs exist or who owns them. The only rejected filter ' +
        'is one that parses to no id at all (`pool_offer_id=abc`), which answers `422 ' +
        '`INVALID_POOL_OFFER_ID`` rather than silently falling through to the unfiltered dump.\n\n' +
        '**`certificate_number`** looks one card up by its exact certificate number, and unlike the ' +
        'listed-only dump it answers across the card’s whole life — each row carries its `status` ' +
        '(`listed` / `unavailable` / `on_hands` / `redeemed`). An unmatched number is an empty page.\n\n' +
        '**Paging is COUNT-free by design.** `meta.has_more` tells you whether more rows remain; pass ' +
        '`meta.last_id` back as `after_id` to walk the whole inventory. Prefer that cursor over `page`: ' +
        '`page` uses OFFSET, so deep pages get progressively more expensive. `?with_total=true` adds ' +
        '`meta.total_count` for the rare client that wants a size up front.',
      operationId: 'operatorListPoolItems',
      security: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description:
            'One pack id, a comma-separated list (`529,565`), or the array form `pool_offer_id[]=529&pool_offer_id[]=565`. ' +
            'Ids that reach nothing are dropped and answer an empty page; only a value that parses to no id at all is rejected.',
        },
        {
          name: 'certificate_number',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description:
            'Exact certificate number of one card of yours. Answers across the card’s whole life, not just listed stock.',
        },
        {
          name: 'after_id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description:
            'Keyset cursor: return only cards with an id greater than this. Pass the previous page’s `meta.last_id`. ' +
            'Takes precedence over `page`.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1 },
          description: '1-based page number. Ignored when `after_id` is given.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Page size. Defaults to 100 and is capped at 200.',
        },
        {
          name: 'with_total',
          in: 'query',
          required: false,
          schema: { type: 'boolean', default: false },
          description: 'Add `meta.total_count` — an extra COUNT over the whole filtered scope. Off by default.',
        },
      ],
      responses: {
        '200': {
          description:
            'Your matching stock. Also the answer for a pack id that is unknown, is not yours, or holds none of your ' +
            'cards — an empty `pool_items` array.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'OperatorPoolItemListResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['pool_items', 'meta'],
                    properties: {
                      pool_items: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'OperatorPoolItem',
                          properties: {
                            id: { type: 'integer', description: 'Card (pool_item) id.' },
                            title: { type: 'string', description: 'Card title.' },
                            image_url: {
                              type: 'string',
                              format: 'uri',
                              nullable: true,
                              description: 'Slab scan if present, otherwise the first uploaded image; may be null.',
                            },
                            fair_market_value: { type: 'number', nullable: true, description: 'Fair market value, in USD.' },
                            certificate_number: {
                              type: 'string',
                              nullable: true,
                              description: 'Grader certificate number; null for ungraded cards.',
                            },
                            status: {
                              type: 'string',
                              enum: ['listed', 'unavailable', 'on_hands', 'redeemed'],
                              description:
                                'Where the card is in its life. The plain dump only ever answers `listed`; the ' +
                                '`certificate_number` lookup answers across all four.',
                            },
                            card_details: {
                              type: 'object',
                              nullable: true,
                              description:
                                'Trimmed enrichment: grade, card name, category and the per-category identity fields ' +
                                '(`pokemon.character` / `pokemon.power_type`, `attributes.player` / `attributes.sport`). ' +
                                'Null when the card has no enrichment.',
                            },
                            is_jackpot_card: {
                              type: 'boolean',
                              description:
                                'Whether this card is currently registered as one of your jackpot cards. A jackpot card ' +
                                'stays in this feed — it is still your stock — but it is out of every draw, out of the ' +
                                "pack's published odds and out of its stock counts.",
                            },
                            jackpot_card_id: {
                              type: 'integer',
                              nullable: true,
                              description:
                                'The jackpot card id backing this card — the handle you echo back in `jackpot: { card_id }`. ' +
                                'Null for ordinary stock. Use it to join this feed against `GET /v1/jackpot_cards` without a ' +
                                'second lookup.',
                            },
                            in_alt: {
                              type: 'boolean',
                              nullable: true,
                              description:
                                'Only present for accounts with Alt presence enabled: whether the card sits in the newest ' +
                                'Alt vault snapshot. Null means unknown (never scraped, or no usable certificate number).',
                            },
                            created_at: { type: 'string', format: 'date-time', description: 'ISO 8601.' },
                            updated_at: { type: 'string', format: 'date-time', description: 'ISO 8601.' },
                          },
                        },
                      },
                      meta: {
                        type: 'object',
                        title: 'OperatorPoolItemListMeta',
                        required: ['page', 'per', 'count', 'has_more'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          per: { type: 'integer', description: 'Page size actually used.' },
                          count: { type: 'integer', description: 'Rows in this page.' },
                          has_more: { type: 'boolean', description: 'Whether more rows remain after this page.' },
                          last_id: {
                            type: 'integer',
                            nullable: true,
                            description: 'Id of the last row in this page — pass it back as `after_id`. Null on an empty page.',
                          },
                          total_count: {
                            type: 'integer',
                            description: 'Total matching rows. Present only when `with_total=true` was passed.',
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  pool_items: [
                    {
                      id: 512901,
                      title: 'Charizard — Base Set Holo PSA 9',
                      image_url: 'https://cdn.repackz.fun/rails/active_storage/.../charizard.png',
                      fair_market_value: 1850.0,
                      certificate_number: '10241337',
                      status: 'listed',
                      card_details: { card: { grade: '9' }, name: 'Charizard', category: 'Pokémon', pokemon: { character: 'Charizard' } },
                      is_jackpot_card: false,
                      jackpot_card_id: null,
                      created_at: '2026-08-01T09:12:44Z',
                      updated_at: '2026-08-20T17:03:02Z',
                    },
                  ],
                  meta: { page: 1, per: 100, count: 1, has_more: false, last_id: 512901 },
                },
              },
            },
          },
        },
        '401': {
          description: 'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE`, or `EXPIRED_TIMESTAMP`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description: '`IP_NOT_WHITELISTED` — the request came from an IP not on your allow-list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            '`INVALID_POOL_OFFER_ID` — `pool_offer_id` was given but parses to no id at all. Note this is NOT the ' +
            'answer for an unknown or foreign pack id, which returns `200` with an empty list.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: '`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': [
        {
          lang: 'ruby',
          label: 'Ruby',
          source: [
            "require 'openssl'; require 'net/http'; require 'uri'",
            "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
            "path = '/loot_machine/api/game_platform/v1/pool_items?pool_offer_id=529&per=100'",
            "body = '' # GET has an empty body",
            'ts = Time.now.to_i.to_s',
            'signing_string = "#{ts}.GET #{path}.#{body}"',
            "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
            'uri = URI("https://api.getcardbase.com#{path}")',
            'req = Net::HTTP::Get.new(uri)',
            'req[\'Authorization\'] = "Bearer #{api_key}"',
            "req['X-Repackz-Timestamp'] = ts",
            "req['X-Repackz-Signature'] = sig",
            'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
            'puts res.body',
          ].join('\n'),
        },
        {
          lang: 'node',
          label: 'Node.js',
          source: [
            "const crypto = require('crypto');",
            'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
            "const path = '/loot_machine/api/game_platform/v1/pool_items?pool_offer_id=529&per=100';",
            "const body = ''; // GET has an empty body",
            'const ts = Math.floor(Date.now() / 1000).toString();',
            'const signingString = `${ts}.GET ${path}.${body}`;',
            "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
            'const res = await fetch(`https://api.getcardbase.com${path}`, {',
            "  method: 'GET',",
            '  headers: {',
            "    'Authorization': `Bearer ${apiKey}`,",
            "    'X-Repackz-Timestamp': ts,",
            "    'X-Repackz-Signature': sig,",
            '  },',
            '});',
            'console.log(await res.json());',
          ].join('\n'),
        },
        {
          lang: 'shell',
          label: 'cURL',
          source: [
            'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
            "PATH_URI='/loot_machine/api/game_platform/v1/pool_items?pool_offer_id=529&per=100'",
            "BODY='' # GET has an empty body",
            'TS=$(date +%s)',
            'SIGNING_STRING="$TS.GET $PATH_URI.$BODY"',
            'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
            'curl -sS "https://api.getcardbase.com$PATH_URI" \\',
            '  -H "Authorization: Bearer $API_KEY" \\',
            '  -H "X-Repackz-Timestamp: $TS" \\',
            '  -H "X-Repackz-Signature: $SIG"',
          ].join('\n'),
        },
      ],
    },
  },
};
